import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import { ResponsiveBar } from '@nivo/bar';
import { ResponsiveFunnel } from '@nivo/funnel';
import { ChartTooltip } from './ChartTooltip';
import {
    BaseDashboardProps,
    HistogramViewOption,
    SortConfig,
    calculateTransferTotals,
    transformEngagementsToCallRecords,
    transformEngagementsToTransferRecords,
    sortDataRecords,
    toggleSort,
    callRecordsToTranscripts,
    transferRecordsToTranscripts,
    isSpanishEngagement
} from './dashboardTypes';

// Styled components
import {
    DashboardGrid,
    ChartTile,
    TallChartTile,
    SmallNumberTile,
    SmallTilesRow,
    WideChartTile,
    TileHeader,
    TileTitle,
    ChartContainer,
    FunnelChartContainer,
    TallChartContainer,
    ChartLoadingOverlay,
    EmptyStateMessage,
    NumberLoadingWrapper,
    NumberDisplay,
    NoSubtextNumberDisplay,
    BigNumber,
    NumberSubtext,
    CallDataModalContent,
    CallDataModalHeader,
    CallDataModalTitleSection,
    CallDataModalTitle,
    CallDataDateRange,
    CallDataModalCloseButton,
    CallDataScrollContainer,
    CallDataTable,
    CallDataTh,
    CallDataRow,
    CallDataTd,
    TranscriptLink,
    TagsContainer,
    CallTag,
    CallDataModalFooter,
    ExportInfoText,
    ExportButton,
    ButtonSpinner,
    SearchInputWrapper,
    FilterSearchInput,
    ClearSearchButton,
    ClearFilterLink,
    FilterActionsContainer,
    ResultsInfo,
    FilterableColumnHeader,
    ColumnHeaderContent,
    ColumnHeaderText,
    ColumnFilterIcon,
    SortIcon,
    ColumnFilterDropdown,
    ColumnFilterHeader,
    ColumnFilterContent,
    ColumnFilterItem,
    ColumnFilterCheckbox,
    PhoneFilterSection,
    PhoneFilterModeToggle,
    PhoneFilterModeButton,
    PhoneFilterList,
    PhoneFilterTag,
    PhoneFilterTagRemove,
    SimpleFilterControlsContainer,
    SimpleFilterRow,
    CategoryBadge,
    TruncatedLocationText,
    HideOnMobile,
    ViewDetailsButton,
    TileHeaderButtonWrapper,
    TileHeaderStatsSection,
    TileHeaderNumber,
    TileHeaderLabel,
    SalesSummaryNumber
} from './ApplicationDashboard.styles';

// Shared imports
import { color_shades_dark, color_shades_darkest, chart_planned_fill_light, chart_planned_solid, chart_unplanned_fill_light, chart_unplanned_solid, text_dark, voicify_spark_light, voicify_spark_dark, voicify_spark_darker, color_shades_darker } from '../../../../../shared/constants/colors';
import SecondaryLoader from '../../../../../shared/components/general/SecondaryLoader';
import HistogramContainer from '../../../../../shared/components/charts/ConvSuccessHistogramContainer';
import HistogramChartCard from '../../../../../shared/components/charts/HistogramChartCard';
import OrgAnalyticsContainer from '../../../../../shared/hooks/OrgAnalyticsContainer';
import EngagementHistogramContainer, { EngagementHistogramView, getVolumeInterval } from '../../../../../shared/hooks/EngagementHistogramContainer';
import { EngagementAnalyticsFilter } from '../../../../../shared/api/controllers/engagementAnalyticsReporting';
import {
    transformEngagementsToConversionFunnel,
    transformToFoodOrderingMetrics,
    transformSeparateTransferReasonsToChartData,
    getEmptyConversionFunnelData,
    ConversionFunnelItem,
    DashboardFoodOrderingMetrics,
    HistogramDataset,
    aggregateTopLevelMetricsFiltered,
    flattenGroupedHistogramDataFiltered,
    flattenConcurrentCallHistogramDataFiltered,
    filterEngagementsByAppIds,
    filterEngagementsByOrganizationIds,
    filterTransferReasonsByAppIds,
    filterTransferReasonsByOrganizationIds,
    parseAnalyticsDate
} from '../../../../../shared/utils/engagementAnalyticsTransformers';
import TransfersChart, { TransferReason, TRANSFER_REASON_LABELS } from '../../../../../shared/components/charts/TransfersChart';
import ModalLayout from '../../../../../shared/components/structure/ModalLayout';
import ChartDetailModal, { ChartDetailData, TranscriptRecord } from './ChartDetailModal';
import CallDataModal from './CallDataModal';
import TransferDataModal from './TransferDataModal';
import useCSVHandler from '../../../../../shared/hooks/UseCSVHandler';
import { getCategoryName, ALL_CATEGORIES } from '../../../../../shared/constants/analyticsConstants/generativeCallReview';
import { useTagConfig } from '../../../../../shared/hooks/TagConfigContainer';
import { MetricInfoTooltip } from './MetricInfoTooltip';
import { nivoChartTheme, funnelChartDefaults, funnelColors, funnelGlassColors, barChartDefaults } from '../../../../../shared/constants/chartTheme';
import { getSparkBaseUrl } from '../../../../../shared/constants/Urls';

const filterIcon = require('../../../../../shared/content/images/spark/filter.svg');

// Note: smallTileBigNumberStyle intentionally removed — BigNumber styled component
// now handles all responsive sizing via breakpoint_macbook_small / breakpoint_large CSS.
const smallTileBigNumberStyle: React.CSSProperties = {};
const smallTileSubtextStyle = { fontSize: '10px' };

// Helper to format hours as "Xh Ym" for Labor Time Saved display
const formatHoursAndMinutes = (hours: number): string => {
    if (hours === undefined || hours === null) return '0h 0m';
    const wholeHours = Math.floor(hours);
    const minutes = Math.round((hours - wholeHours) * 60);
    if (wholeHours === 0) {
        return `${minutes}m`;
    }
    return `${wholeHours}h ${minutes}m`;
};

interface AIOrderingDashboardProps extends BaseDashboardProps { }

interface PlatformMetricBarChartDatum {
    organizationId: string;
    organizationLabel: string;
    organizationName: string;
    orderPlaceRate: number;
    orderAttempts: number;
    placedOrders: number;
    averageTicketSize: number;
    totalGrossRevenue: number;
    questionsAnswered: number;
    laborHoursSaved: number;
    upsellDollarAmount: number;
    upsellRevenueGenerated: number;
}

type PlatformBarChartView = 'orderPlaceRate' | 'placedOrderVolume' | 'averageTicketSize' | 'totalGrossRevenue' | 'questionsAnswered' | 'laborHoursSaved' | 'upsellDollarAmount' | 'upsellRevenueGenerated';

const AIOrderingDashboard: React.FC<AIOrderingDashboardProps> = ({
    organizationId,
    organizationIds,
    startDate,
    endDate,
    shouldLoadData = true,
    selectedAppIds,
    selectedEntityIds,
    isChartsLoading,
    appNameMap,
    entityNameMap,
    isMultiLingual,
    analyticsMode = 'organization',
    representativeAppId,
    isSpanishOnlyFilter,
    onLoadingChange,
    refreshKey
}) => {
    const { getTagName, foodOrderingTags, loadTagConfig } = useTagConfig();
    // Setup containers
    const engagementHistogramContainer = EngagementHistogramContainer.useContainer();
    const orgAnalyticsContainer = OrgAnalyticsContainer.useContainer();
    const entityIds = selectedEntityIds ?? selectedAppIds;
    const isPlatformMode = analyticsMode === 'platform';
    const locationNameMap = isPlatformMode ? (entityNameMap || {}) : appNameMap;
    const groupBy = isPlatformMode ? 'organizationId' as const : 'applicationId' as const;
    const selectedEntityCount = entityIds.size;
    const getEntityDisplayName = useCallback((entityIdOrName: string) => {
        return (locationNameMap && locationNameMap[entityIdOrName]) || (appNameMap && appNameMap[entityIdOrName]) || entityIdOrName;
    }, [locationNameMap, appNameMap]);

    // Load tag config from the first selected application
    useEffect(() => {
        const firstAppId = representativeAppId || selectedAppIds.values().next().value;
        if (firstAppId) {
            loadTagConfig(firstAppId);
        }
    }, [selectedAppIds, representativeAppId]);

    // Sync engagement histogram container with FILTERED org analytics data
    useEffect(() => {
        const filteredCallVolume = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredOrderAttempted = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformOrderAttemptedVolumeHistogramPerOrg : orgAnalyticsContainer.organizationOrderAttemptedVolumeHistogramPerApp,
            entityIds
        );
        const filteredOrderPlaced = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformOrderPlacedVolumeHistogramPerOrg : orgAnalyticsContainer.organizationOrderPlacedVolumeHistogramPerApp,
            entityIds
        );
        const filteredSpanishCallVolume = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredSpanishContainable = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishContainableCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishContainableCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredSpanishContained = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformSpanishContainedCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationSpanishContainedCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredAverageByDay = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformAverageCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationAverageCallVolumeHistogramPerApp,
            entityIds
        );
        const filteredAverageByHour = flattenGroupedHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformAverageCallVolumeByHourHistogramPerOrg : orgAnalyticsContainer.organizationAverageCallVolumeByHourHistogramPerApp,
            entityIds
        );
        // Use max-based aggregation for concurrent calls to avoid inflated values
        const filteredConcurrent = flattenConcurrentCallHistogramDataFiltered(
            isPlatformMode ? orgAnalyticsContainer.platformConcurrentCallVolumeHistogramPerOrg : orgAnalyticsContainer.organizationConcurrentCallVolumeHistogramPerApp,
            entityIds
        );

        engagementHistogramContainer.updateHistogramData({
            callVolumeHistogram: filteredCallVolume,
            orderAttemptedVolumeHistogram: filteredOrderAttempted,
            orderPlacedVolumeHistogram: filteredOrderPlaced,
            spanishCallVolumeHistogram: filteredSpanishCallVolume,
            spanishContainableCallVolumeHistogram: filteredSpanishContainable,
            spanishContainedCallVolumeHistogram: filteredSpanishContained,
            averageCallVolumeByHourHistogram: filteredAverageByHour,
            averageCallVolumeByDayHistogram: filteredAverageByDay,
            concurrentCallVolumeHistogram: filteredConcurrent
        });
    }, [
        orgAnalyticsContainer.organizationCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationOrderAttemptedVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationOrderPlacedVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishContainableCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationSpanishContainedCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationAverageCallVolumeHistogramPerApp,
        orgAnalyticsContainer.organizationAverageCallVolumeByHourHistogramPerApp,
        orgAnalyticsContainer.organizationConcurrentCallVolumeHistogramPerApp,
        orgAnalyticsContainer.platformCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformOrderAttemptedVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformOrderPlacedVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishContainableCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformSpanishContainedCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformAverageCallVolumeHistogramPerOrg,
        orgAnalyticsContainer.platformAverageCallVolumeByHourHistogramPerOrg,
        orgAnalyticsContainer.platformConcurrentCallVolumeHistogramPerOrg,
        entityIds,
        isPlatformMode
    ]);

    // Sync loading state
    useEffect(() => {
        engagementHistogramContainer.updateLoadingState({
            loadingCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram,
            loadingOrderAttempted: isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderAttemptedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderAttemptedVolumeHistogram,
            loadingOrderPlaced: isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderPlacedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderPlacedVolumeHistogram,
            loadingSpanishCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishCallVolumeHistogram,
            loadingSpanishContainable: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishContainableCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishContainableCallVolumeHistogram,
            loadingSpanishContained: isPlatformMode ? orgAnalyticsContainer.loadingPlatformSpanishContainedCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationSpanishContainedCallVolumeHistogram,
            loadingAverageCallVolumeByHour: isPlatformMode ? orgAnalyticsContainer.loadingPlatformAverageCallVolumeByHourHistogram : orgAnalyticsContainer.loadingOrganizationAverageCallVolumeByHourHistogram,
            loadingAverageCallVolumeByDay: isPlatformMode ? orgAnalyticsContainer.loadingPlatformAverageCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationAverageCallVolumeHistogram,
            loadingConcurrentCallVolume: isPlatformMode ? orgAnalyticsContainer.loadingPlatformConcurrentCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationConcurrentCallVolumeHistogram
        });
    }, [
        orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationOrderAttemptedVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationOrderPlacedVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationSpanishContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationAverageCallVolumeByHourHistogram,
        orgAnalyticsContainer.loadingOrganizationAverageCallVolumeHistogram,
        orgAnalyticsContainer.loadingOrganizationConcurrentCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformOrderAttemptedVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformOrderPlacedVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishContainableCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformSpanishContainedCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformAverageCallVolumeByHourHistogram,
        orgAnalyticsContainer.loadingPlatformAverageCallVolumeHistogram,
        orgAnalyticsContainer.loadingPlatformConcurrentCallVolumeHistogram,
        isPlatformMode
    ]);

    const csvHandler = useCSVHandler();

    // When Spanish Only filter is activated, force the histogram to Spanish Calls view
    useEffect(() => {
        if (isSpanishOnlyFilter) {
            engagementHistogramContainer.setSelectedView('Spanish Calls');
        }
    }, [isSpanishOnlyFilter]);

    // Load data when date range changes (not on selectedAppIds change)
    useEffect(() => {
        if (!shouldLoadData) return;
        if ((!organizationId && !organizationIds?.length) || (isPlatformMode && !organizationIds?.length)) return;

        const controller = new AbortController();
        const signal = controller.signal;

        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const baseFilter: EngagementAnalyticsFilter = {
            startDate,
            endDate,
            timezone: browserTimezone,
            signal
        };

        const volumeInterval = getVolumeInterval(startDate, endDate);
        const volumeFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: volumeInterval };
        const averageByDayFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: 1 };
        const averageByHourFilter: EngagementAnalyticsFilter = { ...baseFilter, interval: 0 };

        const loads = isPlatformMode
            ? [
                orgAnalyticsContainer.loadPlatformEngagements(organizationIds, baseFilter),
                orgAnalyticsContainer.loadPlatformTopLevelMetrics(organizationIds, baseFilter),
                orgAnalyticsContainer.loadPlatformCallVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformOrderAttemptedVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformOrderPlacedVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformTransferReasons(organizationIds, baseFilter),
                orgAnalyticsContainer.loadPlatformUpsoldRevenue(organizationIds, baseFilter),
                orgAnalyticsContainer.loadPlatformAverageCallVolumeHistogram(organizationIds, averageByDayFilter),
                orgAnalyticsContainer.loadPlatformAverageCallVolumeByHourHistogram(organizationIds, averageByHourFilter),
                orgAnalyticsContainer.loadPlatformConcurrentCallVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformSpanishCallVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformSpanishContainableCallVolumeHistogram(organizationIds, volumeFilter),
                orgAnalyticsContainer.loadPlatformSpanishContainedCallVolumeHistogram(organizationIds, volumeFilter)
            ]
            : [
                orgAnalyticsContainer.loadOrganizationEngagements(organizationId, baseFilter),
                orgAnalyticsContainer.loadOrganizationTopLevelMetrics(organizationId, baseFilter),
                orgAnalyticsContainer.loadOrganizationCallVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationOrderAttemptedVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationOrderPlacedVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationTransferReasons(organizationId, baseFilter),
                orgAnalyticsContainer.loadOrganizationAverageCallVolumeHistogram(organizationId, averageByDayFilter),
                orgAnalyticsContainer.loadOrganizationAverageCallVolumeByHourHistogram(organizationId, averageByHourFilter),
                orgAnalyticsContainer.loadOrganizationConcurrentCallVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationSpanishCallVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationSpanishContainableCallVolumeHistogram(organizationId, volumeFilter),
                orgAnalyticsContainer.loadOrganizationSpanishContainedCallVolumeHistogram(organizationId, volumeFilter)
            ];

        Promise.all(loads);

        return () => {
            controller.abort();
        };
    }, [organizationId, organizationIds, startDate, endDate, refreshKey, isPlatformMode, shouldLoadData]);

    // ==================== GRANULAR LOADING STATES ====================
    // Individual loading states for each chart type - allows charts to render independently
    // as their data becomes available, improving perceived performance for large orgs

    // Order Conversion Funnel - depends on organizationEngagements
    const isFunnelLoading = isChartsLoading || (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements);

    // Transfers chart - depends on transfer reasons data and engagement data for modal
    const isTransfersLoading = isChartsLoading ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTransferReasons : orgAnalyticsContainer.loadingOrganizationTransferReasons) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements);

    // Small tiles (Upsells, Sales, Questions, Labor) - depend on top-level metrics and engagement data for modals
    const isMetricTilesLoading = isChartsLoading ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTopLevelMetrics : orgAnalyticsContainer.loadingOrganizationTopLevelMetrics) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements);

    // Histogram - depends on histogram data from container
    const isHistogramLoading = engagementHistogramContainer.isLoading ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderAttemptedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderAttemptedVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderPlacedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderPlacedVolumeHistogram);
    const isHistogramChartLoading = isChartsLoading || isHistogramLoading;

    // Engagement data loading - needed for modals and export (heavier computation)
    // Define this first so it can be used in isChartDataLoading
    const isEngagementDataLoading = isPlatformMode ? orgAnalyticsContainer.loadingPlatformEngagements : orgAnalyticsContainer.loadingOrganizationEngagements;

    // Combined loading state for parent reporting - any chart still loading
    // IMPORTANT: Include isEngagementDataLoading so chart loaders stay visible until modal data is ready
    const isChartDataLoading =
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTopLevelMetrics : orgAnalyticsContainer.loadingOrganizationTopLevelMetrics) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformCallVolumeHistogram : orgAnalyticsContainer.loadingOrganizationCallVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderAttemptedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderAttemptedVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformOrderPlacedVolumeHistogram : orgAnalyticsContainer.loadingOrganizationOrderPlacedVolumeHistogram) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformTransferReasons : orgAnalyticsContainer.loadingOrganizationTransferReasons) ||
        (isPlatformMode ? orgAnalyticsContainer.loadingPlatformUpsoldRevenue : false) ||
        isEngagementDataLoading ||
        engagementHistogramContainer.isLoading;

    // Primary loading state for parent component reporting
    const isChartsLoadingState = isChartsLoading || isChartDataLoading;

    // Track if interactive features are ready (after engagement data loads and transforms)
    // This uses a delayed state update via setTimeout to allow UI to render first
    const [isInteractiveFeaturesReady, setIsInteractiveFeaturesReady] = useState(false);

    // When engagement data finishes loading, enable interactive features after a micro-delay
    // This allows the main thread to finish rendering charts before computing modal data
    // IMPORTANT: Buttons should be disabled while ANY loading is happening
    useEffect(() => {
        const isAnyLoading = isEngagementDataLoading || isChartDataLoading || isChartsLoading;

        if (!isAnyLoading) {
            // Use requestIdleCallback if available, otherwise setTimeout
            const enableInteractive = () => setIsInteractiveFeaturesReady(true);
            if ('requestIdleCallback' in window) {
                (window as any).requestIdleCallback(enableInteractive, { timeout: 100 });
            } else {
                setTimeout(enableInteractive, 50);
            }
        } else {
            setIsInteractiveFeaturesReady(false);
        }
    }, [isEngagementDataLoading, isChartDataLoading, isChartsLoading]);

    // Also disable interactive features when charts are loading (for immediate visual feedback)
    const isButtonsDisabled = !isInteractiveFeaturesReady || isChartsLoadingState;

    // Detect if "today" date range is selected - data may still be loading
    const isTodayDateRange = parseAnalyticsDate(startDate).isSame(moment(), 'day') && parseAnalyticsDate(endDate).isSame(moment(), 'day');

    // Report only chart loading state to parent (not the heavy engagement computation)
    useEffect(() => {
        onLoadingChange?.(isChartsLoadingState);
    }, [isChartsLoadingState, onLoadingChange]);

    // UI State
    const [hoveredTile, setHoveredTile] = useState<'upsell' | 'sales' | 'ticket' | 'questions' | 'orderAttemptRate' | 'augmentedOrderAttemptRate' | 'laborTime' | null>(null);
    const [isMobile, setIsMobile] = useState(false);

    // Detect mobile viewport
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 640);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);
    const [isChartDetailModalOpen, setIsChartDetailModalOpen] = useState<boolean>(false);
    const [selectedFunnelData, setSelectedFunnelData] = useState<ChartDetailData[] | null>(null);
    const [chartDetailTitle, setChartDetailTitle] = useState<string>('');
    const [chartDetailValueLabel, setChartDetailValueLabel] = useState<string>('conversations');
    const [chartDetailCountPrefix, setChartDetailCountPrefix] = useState<string>('');
    const [chartDetailShowPercentOfTotal, setChartDetailShowPercentOfTotal] = useState<boolean>(true);
    const [chartDetailSummaryLabel, setChartDetailSummaryLabel] = useState<string | undefined>(undefined);
    const [chartDetailTranscripts, setChartDetailTranscripts] = useState<TranscriptRecord[]>([]);
    const [showTranscriptsTab, setShowTranscriptsTab] = useState<boolean>(false);
    const [chartDetailHeaderInfo, setChartDetailHeaderInfo] = useState<string | undefined>(undefined);
    const [isCallDataModalOpen, setIsCallDataModalOpen] = useState<boolean>(false);
    const [isTransfersModalOpen, setIsTransfersModalOpen] = useState<boolean>(false);
    const [isOrderAttemptsModalOpen, setIsOrderAttemptsModalOpen] = useState<boolean>(false);
    const [isPlacedOrdersModalOpen, setIsPlacedOrdersModalOpen] = useState<boolean>(false);
    const [platformBarChartView, setPlatformBarChartView] = useState<PlatformBarChartView>('orderPlaceRate');

    // Call data filter state
    const [callSearchQuery, setCallSearchQuery] = useState<string>('');
    const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set());
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(new Set());
    const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
    const [openCallColumnFilter, setOpenCallColumnFilter] = useState<string | null>(null);
    const [selectedCallPhones, setSelectedCallPhones] = useState<Set<string>>(new Set());
    const [callPhoneFilterMode, setCallPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedCallLocations, setSelectedCallLocations] = useState<Set<string>>(new Set());

    // Order Attempts filter state
    const [orderAttemptsSearchQuery, setOrderAttemptsSearchQuery] = useState<string>('');
    const [selectedOrderAttemptsTags, setSelectedOrderAttemptsTags] = useState<Set<string>>(new Set());
    const [orderAttemptsSortConfig, setOrderAttemptsSortConfig] = useState<SortConfig | null>(null);
    const [openOrderAttemptsColumnFilter, setOpenOrderAttemptsColumnFilter] = useState<string | null>(null);
    const [selectedOrderAttemptsPhones, setSelectedOrderAttemptsPhones] = useState<Set<string>>(new Set());
    const [orderAttemptsPhoneFilterMode, setOrderAttemptsPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedOrderAttemptsLocations, setSelectedOrderAttemptsLocations] = useState<Set<string>>(new Set());
    const [orderAttemptsDataPage, setOrderAttemptsDataPage] = useState<number>(0);

    // Placed Orders filter state
    const [placedOrdersSearchQuery, setPlacedOrdersSearchQuery] = useState<string>('');
    const [selectedPlacedOrdersTags, setSelectedPlacedOrdersTags] = useState<Set<string>>(new Set());
    const [placedOrdersSortConfig, setPlacedOrdersSortConfig] = useState<SortConfig | null>(null);
    const [openPlacedOrdersColumnFilter, setOpenPlacedOrdersColumnFilter] = useState<string | null>(null);
    const [selectedPlacedOrdersPhones, setSelectedPlacedOrdersPhones] = useState<Set<string>>(new Set());
    const [placedOrdersPhoneFilterMode, setPlacedOrdersPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedPlacedOrdersLocations, setSelectedPlacedOrdersLocations] = useState<Set<string>>(new Set());
    const [placedOrdersDataPage, setPlacedOrdersDataPage] = useState<number>(0);

    // Transfer filter state
    const [transferSearchQuery, setTransferSearchQuery] = useState<string>('');
    const [selectedTransferType, setSelectedTransferType] = useState<'all' | 'Planned' | 'Unplanned' | 'Undetermined'>('all');
    const [selectedTransferReasons, setSelectedTransferReasons] = useState<Set<TransferReason>>(new Set());
    const [transferSortConfig, setTransferSortConfig] = useState<SortConfig | null>(null);
    const [openTransferColumnFilter, setOpenTransferColumnFilter] = useState<string | null>(null);
    const [selectedTransferPhones, setSelectedTransferPhones] = useState<Set<string>>(new Set());
    const [transferPhoneFilterMode, setTransferPhoneFilterMode] = useState<'include' | 'exclude'>('include');
    const [selectedTransferLocations, setSelectedTransferLocations] = useState<Set<string>>(new Set());

    // Pagination state for modals - prevents rendering thousands of rows at once
    const PAGE_SIZE = 100;
    const [callDataPage, setCallDataPage] = useState<number>(0);
    const [transferDataPage, setTransferDataPage] = useState<number>(0);

    // Export loading states
    const [isExportingCallData, setIsExportingCallData] = useState<boolean>(false);
    const [isExportingTransfers, setIsExportingTransfers] = useState<boolean>(false);
    const [isExportingOrderAttempts, setIsExportingOrderAttempts] = useState<boolean>(false);
    const [isExportingPlacedOrders, setIsExportingPlacedOrders] = useState<boolean>(false);

    // Spanish Calls modal state
    const [isSpanishCallsModalOpen, setIsSpanishCallsModalOpen] = useState<boolean>(false);

    // ==================== DEFERRED DATA TRANSFORMATIONS ====================
    // Heavy computations for modals/export - only computed when interactive features are ready
    // This allows charts to render first without waiting for these expensive transformations

    const engagements = isPlatformMode ? orgAnalyticsContainer.platformEngagements : orgAnalyticsContainer.organizationEngagements;

    // Filter engagements by selectedAppIds and Spanish-only filter
    const filteredEngagements = useMemo(() => {
        if (isPlatformMode) {
            let filtered = filterEngagementsByOrganizationIds(engagements, entityIds);
            if (isSpanishOnlyFilter && filtered) {
                filtered = filtered.filter(isSpanishEngagement);
            }
            return filtered;
        }
        else {
            let filtered = filterEngagementsByAppIds(engagements, selectedAppIds);
            if (isSpanishOnlyFilter && filtered) {
                filtered = filtered.filter(isSpanishEngagement);
            }
            return filtered;
        }
    }, [engagements, selectedAppIds, entityIds, isPlatformMode, isSpanishOnlyFilter]);

    // Count of all conversations for the histogram legend.
    // Platform mode shows all loaded engagements; organization mode excludes NO_INTERACTION.
    const allConversationsCount = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return 0;
        return isPlatformMode
            ? filteredEngagements.length
            : filteredEngagements.filter(e => e.sessionCategory !== 'NO_INTERACTION').length;
    }, [filteredEngagements, isPlatformMode]);

    const orderAttemptRateMetrics = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) {
            return {
                totalCalls: 0,
                orderAttemptCalls: 0,
                orderAttemptRate: 0,
                apps: [] as { appName: string; count: number; english: number; spanish: number }[]
            };
        }

        const breakdownByEntity = filteredEngagements.reduce((acc, engagement) => {
            const entityId = isPlatformMode
                ? (engagement.organizationId || 'unknown')
                : (engagement.applicationId || 'unknown');
            const isSpanish = isSpanishEngagement(engagement);

            if (!acc[entityId]) {
                acc[entityId] = {
                    count: 0,
                    english: 0,
                    spanish: 0
                };
            }

            if (engagement.sessionCategory === 'ACTION_ATTEMPTED') {
                acc[entityId].count++;
                if (isSpanish) {
                    acc[entityId].spanish++;
                } else {
                    acc[entityId].english++;
                }
            }

            return acc;
        }, {} as Record<string, { count: number; english: number; spanish: number }>);

        const totalCalls = filteredEngagements.length;
        const orderAttemptCalls = filteredEngagements.filter(engagement => engagement.sessionCategory === 'ACTION_ATTEMPTED').length;
        const orderAttemptRate = totalCalls > 0
            ? Math.round((orderAttemptCalls / totalCalls) * 1000) / 10
            : 0;

        const apps = Object.entries(breakdownByEntity)
            .filter(([, value]) => value.count > 0)
            .map(([entityId, value]) => ({
                appName: getEntityDisplayName(entityId),
                count: value.count,
                english: value.english,
                spanish: value.spanish
            }))
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }

                return a.appName.localeCompare(b.appName);
            });

        return {
            totalCalls,
            orderAttemptCalls,
            orderAttemptRate,
            apps
        };
    }, [filteredEngagements, getEntityDisplayName, isPlatformMode]);

    const augmentedOrderAttemptRateMetrics = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) {
            return {
                eligibleCalls: 0,
                orderAttemptCalls: 0,
                orderAttemptRate: 0,
                apps: [] as { appName: string; count: number; english: number; spanish: number }[]
            };
        }

        const eligibleEngagements = filteredEngagements.filter(engagement => {
            const tags = engagement.sessionTags || [];
            return engagement.sessionCategory !== 'NO_INTERACTION' && !tags.includes('CALL_DURING_OFF_HOURS');
        });

        const breakdownByEntity = eligibleEngagements.reduce((acc, engagement) => {
            const entityId = isPlatformMode
                ? (engagement.organizationId || 'unknown')
                : (engagement.applicationId || 'unknown');
            const isSpanish = isSpanishEngagement(engagement);

            if (!acc[entityId]) {
                acc[entityId] = {
                    count: 0,
                    english: 0,
                    spanish: 0
                };
            }

            if (engagement.sessionCategory === 'ACTION_ATTEMPTED') {
                acc[entityId].count++;
                if (isSpanish) {
                    acc[entityId].spanish++;
                } else {
                    acc[entityId].english++;
                }
            }

            return acc;
        }, {} as Record<string, { count: number; english: number; spanish: number }>);

        const eligibleCalls = eligibleEngagements.length;
        const orderAttemptCalls = eligibleEngagements.filter(engagement => engagement.sessionCategory === 'ACTION_ATTEMPTED').length;
        const orderAttemptRate = eligibleCalls > 0
            ? Math.round((orderAttemptCalls / eligibleCalls) * 1000) / 10
            : 0;

        const apps = Object.entries(breakdownByEntity)
            .filter(([, value]) => value.count > 0)
            .map(([entityId, value]) => ({
                appName: getEntityDisplayName(entityId),
                count: value.count,
                english: value.english,
                spanish: value.spanish
            }))
            .sort((a, b) => {
                if (b.count !== a.count) {
                    return b.count - a.count;
                }

                return a.appName.localeCompare(b.appName);
            });

        return {
            eligibleCalls,
            orderAttemptCalls,
            orderAttemptRate,
            apps
        };
    }, [filteredEngagements, getEntityDisplayName, isPlatformMode]);

    const platformSmallTilesRowStyle: React.CSSProperties | undefined = isPlatformMode
        ? {
            display: 'grid',
            gridTemplateColumns: 'repeat(6, minmax(0, 1fr))',
            gap: '12px',
            alignItems: 'stretch'
        }
        : undefined;

    const sharedPlatformTileStyle: React.CSSProperties | undefined = isPlatformMode
        ? {
            minWidth: 0,
            padding: '14px 10px'
        }
        : undefined;

    const smallTileStyle: React.CSSProperties = isPlatformMode
        ? {
            ...sharedPlatformTileStyle,
            cursor: isMetricTilesLoading ? 'default' : 'pointer'
        }
        : {
            cursor: isMetricTilesLoading ? 'default' : 'pointer',
            flex: '1 1 0',
            minWidth: 0,
            gridColumn: 'auto'
        };

    const laborTileStyle: React.CSSProperties = isPlatformMode
        ? {
            ...sharedPlatformTileStyle,
            cursor: isMetricTilesLoading || moment(endDate).isBefore('2026-02-01') ? 'default' : 'pointer'
        }
        : {
            cursor: isMetricTilesLoading || moment(endDate).isBefore('2026-02-01') ? 'default' : 'pointer',
            flex: '1 1 0',
            minWidth: 0,
            gridColumn: 'auto'
        };

    const placeholderTileStyle: React.CSSProperties | undefined = isPlatformMode
        ? {
            ...sharedPlatformTileStyle,
            cursor: isMetricTilesLoading ? 'default' : 'pointer'
        }
        : undefined;

    const compactTileTitleStyle: React.CSSProperties | undefined = isPlatformMode
        ? { fontSize: '11px', whiteSpace: 'nowrap' }
        : undefined;

    const compactBigNumberStyle: React.CSSProperties | undefined = isPlatformMode
        ? { fontSize: '26px', letterSpacing: '-0.4px' }
        : undefined;

    const compactSubtextStyle: React.CSSProperties | undefined = isPlatformMode
        ? { fontSize: '9px', marginTop: '5px', maxWidth: '100px', lineHeight: 1.25 }
        : smallTileSubtextStyle;

    const compactSalesSummaryValueStyle: React.CSSProperties | undefined = isPlatformMode
        ? { fontSize: '14px' }
        : undefined;

    const compactSalesSummaryLabelStyle: React.CSSProperties = isPlatformMode
        ? { fontSize: '8px', color: color_shades_dark, lineHeight: 1.2 }
        : { fontSize: '10px', color: color_shades_dark };

    const funnelTheme = useMemo(() => ({
        ...nivoChartTheme,
        labels: {
            ...nivoChartTheme.labels,
            text: {
                ...nivoChartTheme.labels.text,
                fontSize: isPlatformMode ? 15 : 13,
                fontWeight: 700
            }
        }
    }), [isPlatformMode]);

    // Transform data ONLY when interactive features are ready (after charts load)
    const foodOrderingCallDataRecords = useMemo(() => {
        // Only compute when engagement data is available and interactive features are ready
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        return transformEngagementsToCallRecords(filteredEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    const foodOrderingTransferDataRecords = useMemo(() => {
        // Only compute when engagement data is available and interactive features are ready
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        return transformEngagementsToTransferRecords(filteredEngagements, appNameMap, locationNameMap, groupBy);
    }, [filteredEngagements, appNameMap, locationNameMap, groupBy, isInteractiveFeaturesReady]);

    // Spanish-filtered call records for the Spanish Calls modal
    const spanishCallDataRecords = useMemo(() => {
        if (!isInteractiveFeaturesReady || !filteredEngagements || filteredEngagements.length === 0) return [];
        const spanishEngagements = filteredEngagements.filter(isSpanishEngagement);
        return transformEngagementsToCallRecords(spanishEngagements, appNameMap);
    }, [filteredEngagements, appNameMap, isInteractiveFeaturesReady]);

    // Unique values for filters - computed lazily
    const uniqueCallPhones = useMemo(() =>
        foodOrderingCallDataRecords.length > 0
            ? [...new Set(foodOrderingCallDataRecords.map(c => c.phoneLastFour))].sort()
            : [],
        [foodOrderingCallDataRecords]
    );
    const uniqueCallLocations = useMemo(() =>
        foodOrderingCallDataRecords.length > 0
            ? [...new Set(foodOrderingCallDataRecords.map(c => c.location))].sort()
            : [],
        [foodOrderingCallDataRecords]
    );
    const uniqueTransferPhones = useMemo(() =>
        foodOrderingTransferDataRecords.length > 0
            ? [...new Set(foodOrderingTransferDataRecords.map(t => t.phoneLastFour))].sort()
            : [],
        [foodOrderingTransferDataRecords]
    );
    const uniqueTransferLocations = useMemo(() =>
        foodOrderingTransferDataRecords.length > 0
            ? [...new Set(foodOrderingTransferDataRecords.map(t => t.location))].sort()
            : [],
        [foodOrderingTransferDataRecords]
    );
    const uniqueTransferReasons = useMemo(() =>
        foodOrderingTransferDataRecords.length > 0
            ? [...new Set(foodOrderingTransferDataRecords.map(t => t.transferReason))]
            : [],
        [foodOrderingTransferDataRecords]
    );

    // Base data for Order Attempts (calls with category ACTION_ATTEMPTED)
    const orderAttemptsBaseRecords = useMemo(() => {
        return foodOrderingCallDataRecords.filter(call => call.category === 'ACTION_ATTEMPTED');
    }, [foodOrderingCallDataRecords]);

    // Unique values for Order Attempts modal filters
    const uniqueOrderAttemptsPhones = useMemo(() =>
        orderAttemptsBaseRecords.length > 0
            ? [...new Set(orderAttemptsBaseRecords.map(c => c.phoneLastFour))].sort()
            : [],
        [orderAttemptsBaseRecords]
    );
    const uniqueOrderAttemptsLocations = useMemo(() =>
        orderAttemptsBaseRecords.length > 0
            ? [...new Set(orderAttemptsBaseRecords.map(c => c.location))].sort()
            : [],
        [orderAttemptsBaseRecords]
    );

    // Base data for Placed Orders (calls with category ACTION_ATTEMPTED and ACTION_COMPLETED tag)
    const placedOrdersBaseRecords = useMemo(() => {
        return foodOrderingCallDataRecords.filter(call =>
            call.category === 'ACTION_ATTEMPTED' && call.tags.includes('ACTION_COMPLETED')
        );
    }, [foodOrderingCallDataRecords]);

    // Unique values for Placed Orders modal filters
    const uniquePlacedOrdersPhones = useMemo(() =>
        placedOrdersBaseRecords.length > 0
            ? [...new Set(placedOrdersBaseRecords.map(c => c.phoneLastFour))].sort()
            : [],
        [placedOrdersBaseRecords]
    );
    const uniquePlacedOrdersLocations = useMemo(() =>
        placedOrdersBaseRecords.length > 0
            ? [...new Set(placedOrdersBaseRecords.map(c => c.location))].sort()
            : [],
        [placedOrdersBaseRecords]
    );

    // Filtered data - computed only when modal is open to avoid blocking UI
    const filteredFoodOrderingCallData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (foodOrderingCallDataRecords.length === 0 || !isCallDataModalOpen) return [];

        let filtered = foodOrderingCallDataRecords.filter(call => {
            const searchLower = callSearchQuery.toLowerCase();
            const matchesSearch = !callSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.category.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedCallPhones.size > 0) {
                matchesPhone = callPhoneFilterMode === 'include'
                    ? selectedCallPhones.has(call.phoneLastFour)
                    : !selectedCallPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedCallLocations.size === 0 || selectedCallLocations.has(call.location);
            const matchesTags = selectedTags.size === 0 || call.tags.some(tag => selectedTags.has(tag));
            const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(call.category);

            return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
        });

        return sortDataRecords(filtered, sortConfig);
    }, [foodOrderingCallDataRecords, callSearchQuery, selectedCallPhones, callPhoneFilterMode, selectedCallLocations, selectedTags, selectedCategories, sortConfig, isCallDataModalOpen]);

    const filteredTransferData = useMemo(() => {
        // Skip filtering when no data or modal isn't visible
        if (foodOrderingTransferDataRecords.length === 0 || !isTransfersModalOpen) return [];

        let filtered = foodOrderingTransferDataRecords.filter(transfer => {
            const searchLower = transferSearchQuery.toLowerCase();
            const matchesSearch = !transferSearchQuery ||
                transfer.location.toLowerCase().includes(searchLower) ||
                transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                transfer.date.toLowerCase().includes(searchLower) ||
                transfer.time.toLowerCase().includes(searchLower) ||
                transfer.transferType.toLowerCase().includes(searchLower) ||
                (TRANSFER_REASON_LABELS[transfer.transferReason] || transfer.transferReason).toLowerCase().includes(searchLower);

            let matchesPhone = true;
            if (selectedTransferPhones.size > 0) {
                matchesPhone = transferPhoneFilterMode === 'include'
                    ? selectedTransferPhones.has(transfer.phoneLastFour)
                    : !selectedTransferPhones.has(transfer.phoneLastFour);
            }

            const matchesLocation = selectedTransferLocations.size === 0 || selectedTransferLocations.has(transfer.location);
            const matchesType = selectedTransferType === 'all' || transfer.transferType === selectedTransferType;
            const matchesReason = selectedTransferReasons.size === 0 || selectedTransferReasons.has(transfer.transferReason);

            return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
        });

        return sortDataRecords(filtered, transferSortConfig, true);
    }, [foodOrderingTransferDataRecords, transferSearchQuery, selectedTransferPhones, transferPhoneFilterMode, selectedTransferLocations, selectedTransferType, selectedTransferReasons, transferSortConfig, isTransfersModalOpen]);

    // Filtered Order Attempts data - computed only when modal is open
    const filteredOrderAttemptsData = useMemo(() => {
        if (orderAttemptsBaseRecords.length === 0 || !isOrderAttemptsModalOpen) return [];

        let filtered = orderAttemptsBaseRecords.filter(call => {
            const searchLower = orderAttemptsSearchQuery.toLowerCase();
            const matchesSearch = !orderAttemptsSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedOrderAttemptsPhones.size > 0) {
                matchesPhone = orderAttemptsPhoneFilterMode === 'include'
                    ? selectedOrderAttemptsPhones.has(call.phoneLastFour)
                    : !selectedOrderAttemptsPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedOrderAttemptsLocations.size === 0 || selectedOrderAttemptsLocations.has(call.location);
            const matchesTags = selectedOrderAttemptsTags.size === 0 || call.tags.some(tag => selectedOrderAttemptsTags.has(tag));

            return matchesSearch && matchesPhone && matchesLocation && matchesTags;
        });

        return sortDataRecords(filtered, orderAttemptsSortConfig);
    }, [orderAttemptsBaseRecords, orderAttemptsSearchQuery, selectedOrderAttemptsPhones, orderAttemptsPhoneFilterMode, selectedOrderAttemptsLocations, selectedOrderAttemptsTags, orderAttemptsSortConfig, isOrderAttemptsModalOpen]);

    // Filtered Placed Orders data - computed only when modal is open
    const filteredPlacedOrdersData = useMemo(() => {
        if (placedOrdersBaseRecords.length === 0 || !isPlacedOrdersModalOpen) return [];

        let filtered = placedOrdersBaseRecords.filter(call => {
            const searchLower = placedOrdersSearchQuery.toLowerCase();
            const matchesSearch = !placedOrdersSearchQuery ||
                call.location.toLowerCase().includes(searchLower) ||
                call.phoneLastFour.toLowerCase().includes(searchLower) ||
                call.date.toLowerCase().includes(searchLower) ||
                call.time.toLowerCase().includes(searchLower) ||
                call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

            let matchesPhone = true;
            if (selectedPlacedOrdersPhones.size > 0) {
                matchesPhone = placedOrdersPhoneFilterMode === 'include'
                    ? selectedPlacedOrdersPhones.has(call.phoneLastFour)
                    : !selectedPlacedOrdersPhones.has(call.phoneLastFour);
            }

            const matchesLocation = selectedPlacedOrdersLocations.size === 0 || selectedPlacedOrdersLocations.has(call.location);
            const matchesTags = selectedPlacedOrdersTags.size === 0 || call.tags.some(tag => selectedPlacedOrdersTags.has(tag));

            return matchesSearch && matchesPhone && matchesLocation && matchesTags;
        });

        return sortDataRecords(filtered, placedOrdersSortConfig);
    }, [placedOrdersBaseRecords, placedOrdersSearchQuery, selectedPlacedOrdersPhones, placedOrdersPhoneFilterMode, selectedPlacedOrdersLocations, selectedPlacedOrdersTags, placedOrdersSortConfig, isPlacedOrdersModalOpen]);

    // Paginated data for rendering - limits DOM nodes to PAGE_SIZE at a time
    const paginatedCallData = useMemo(() => {
        const start = callDataPage * PAGE_SIZE;
        return filteredFoodOrderingCallData.slice(start, start + PAGE_SIZE);
    }, [filteredFoodOrderingCallData, callDataPage]);

    const paginatedTransferData = useMemo(() => {
        const start = transferDataPage * PAGE_SIZE;
        return filteredTransferData.slice(start, start + PAGE_SIZE);
    }, [filteredTransferData, transferDataPage]);

    const paginatedOrderAttemptsData = useMemo(() => {
        const start = orderAttemptsDataPage * PAGE_SIZE;
        return filteredOrderAttemptsData.slice(start, start + PAGE_SIZE);
    }, [filteredOrderAttemptsData, orderAttemptsDataPage]);

    const paginatedPlacedOrdersData = useMemo(() => {
        const start = placedOrdersDataPage * PAGE_SIZE;
        return filteredPlacedOrdersData.slice(start, start + PAGE_SIZE);
    }, [filteredPlacedOrdersData, placedOrdersDataPage]);

    // Pagination helpers
    const totalCallPages = Math.ceil(filteredFoodOrderingCallData.length / PAGE_SIZE);
    const totalTransferPages = Math.ceil(filteredTransferData.length / PAGE_SIZE);
    const totalOrderAttemptsPages = Math.ceil(filteredOrderAttemptsData.length / PAGE_SIZE);
    const totalPlacedOrdersPages = Math.ceil(filteredPlacedOrdersData.length / PAGE_SIZE);

    // Reset pagination when filters change
    useEffect(() => { setCallDataPage(0); }, [callSearchQuery, selectedCallPhones, callPhoneFilterMode, selectedCallLocations, selectedTags, selectedCategories, sortConfig]);
    useEffect(() => { setTransferDataPage(0); }, [transferSearchQuery, selectedTransferPhones, transferPhoneFilterMode, selectedTransferLocations, selectedTransferType, selectedTransferReasons, transferSortConfig]);
    useEffect(() => { setOrderAttemptsDataPage(0); }, [orderAttemptsSearchQuery, selectedOrderAttemptsPhones, orderAttemptsPhoneFilterMode, selectedOrderAttemptsLocations, selectedOrderAttemptsTags, orderAttemptsSortConfig]);
    useEffect(() => { setPlacedOrdersDataPage(0); }, [placedOrdersSearchQuery, selectedPlacedOrdersPhones, placedOrdersPhoneFilterMode, selectedPlacedOrdersLocations, selectedPlacedOrdersTags, placedOrdersSortConfig]);

    // Filter helpers
    const handleSort = (key: string) => setSortConfig(prev => toggleSort(key, prev));
    const handleTransferSort = (key: string) => setTransferSortConfig(prev => toggleSort(key, prev));

    const handleAddCallPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedCallPhones);
        if (newSet.has(phone)) newSet.delete(phone);
        else newSet.add(phone);
        setSelectedCallPhones(newSet);
    };

    const handleAddTransferPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedTransferPhones);
        if (newSet.has(phone)) newSet.delete(phone);
        else newSet.add(phone);
        setSelectedTransferPhones(newSet);
    };

    const handleAddOrderAttemptsPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedOrderAttemptsPhones);
        if (newSet.has(phone)) newSet.delete(phone);
        else newSet.add(phone);
        setSelectedOrderAttemptsPhones(newSet);
    };

    const handleAddPlacedOrdersPhoneFilter = (phone: string) => {
        const newSet = new Set(selectedPlacedOrdersPhones);
        if (newSet.has(phone)) newSet.delete(phone);
        else newSet.add(phone);
        setSelectedPlacedOrdersPhones(newSet);
    };

    const handleOrderAttemptsSort = (key: string) => setOrderAttemptsSortConfig(prev => toggleSort(key, prev));
    const handlePlacedOrdersSort = (key: string) => setPlacedOrdersSortConfig(prev => toggleSort(key, prev));

    const clearAllCallFilters = () => {
        setCallSearchQuery('');
        setSelectedCallPhones(new Set());
        setCallPhoneFilterMode('include');
        setSelectedCallLocations(new Set());
        setSelectedTags(new Set());
        setSelectedCategories(new Set());
        setSortConfig(null);
        setOpenCallColumnFilter(null);
    };

    const clearAllTransferFilters = () => {
        setTransferSearchQuery('');
        setSelectedTransferPhones(new Set());
        setTransferPhoneFilterMode('include');
        setSelectedTransferLocations(new Set());
        setSelectedTransferType('all');
        setSelectedTransferReasons(new Set());
        setTransferSortConfig(null);
        setOpenTransferColumnFilter(null);
    };

    const clearAllOrderAttemptsFilters = () => {
        setOrderAttemptsSearchQuery('');
        setSelectedOrderAttemptsPhones(new Set());
        setOrderAttemptsPhoneFilterMode('include');
        setSelectedOrderAttemptsLocations(new Set());
        setSelectedOrderAttemptsTags(new Set());
        setOrderAttemptsSortConfig(null);
        setOpenOrderAttemptsColumnFilter(null);
    };

    const clearAllPlacedOrdersFilters = () => {
        setPlacedOrdersSearchQuery('');
        setSelectedPlacedOrdersPhones(new Set());
        setPlacedOrdersPhoneFilterMode('include');
        setSelectedPlacedOrdersLocations(new Set());
        setSelectedPlacedOrdersTags(new Set());
        setPlacedOrdersSortConfig(null);
        setOpenPlacedOrdersColumnFilter(null);
    };

    const hasActiveCallFilters = callSearchQuery || selectedCallPhones.size > 0 || selectedCallLocations.size > 0 || selectedTags.size > 0 || selectedCategories.size > 0;
    const hasActiveTransferFilters = transferSearchQuery || selectedTransferPhones.size > 0 || selectedTransferLocations.size > 0 || selectedTransferType !== 'all' || selectedTransferReasons.size > 0;
    const hasActiveOrderAttemptsFilters = orderAttemptsSearchQuery || selectedOrderAttemptsPhones.size > 0 || selectedOrderAttemptsLocations.size > 0 || selectedOrderAttemptsTags.size > 0;
    const hasActivePlacedOrdersFilters = placedOrdersSearchQuery || selectedPlacedOrdersPhones.size > 0 || selectedPlacedOrdersLocations.size > 0 || selectedPlacedOrdersTags.size > 0;

    // ==================== CHART DATA (PRIORITY - renders first) ====================
    // These are lightweight and needed immediately for chart display

    // Determine if only one app is selected (for Concurrent Calls option visibility)
    const isSingleAppSelected = selectedEntityCount === 1;

    // Reset to All Conversations if Concurrent Calls is selected but multiple apps are now selected
    useEffect(() => {
        if (!isSingleAppSelected && engagementHistogramContainer.selectedView === 'Concurrent Calls') {
            engagementHistogramContainer.setSelectedView('All Conversations');
        }
    }, [isSingleAppSelected, engagementHistogramContainer.selectedView]);

    // Histogram data - rename IDs for AI Ordering context
    const selectedView = (engagementHistogramContainer.selectedView || "All Conversations") as HistogramViewOption;
    const currentHistogramData = useMemo((): HistogramDataset[] => {
        const apiData = engagementHistogramContainer.currentHistogramData;
        if (!apiData || apiData.length === 0) return [];
        // Rename IDs for AI Ordering context
        const labelMap: Record<string, string> = {
            'Containable Conversations': 'Order Attempts',
            'Contained Conversations': 'Placed Orders',
            'Spanish Containable Conversations': 'Spanish Order Attempts',
            'Spanish Contained Conversations': 'Spanish Placed Orders'
        };
        return apiData.map(dataset => ({
            ...dataset,
            id: labelMap[dataset.id] || dataset.id
        }));
    }, [engagementHistogramContainer.currentHistogramData]);

    // Funnel chart configuration
    // Set to true to include Total Calls in the funnel chart display
    const SHOW_TOTAL_CALLS_IN_FUNNEL = false;

    // Funnel colors
    const funnelColorConfig = useMemo(() => ({
        totalCalls: funnelGlassColors[0],
        orderAttempts: funnelGlassColors[1],
        ordersPlaced: funnelGlassColors[2]
    }), []);

    // Transform funnel data - filter by selectedAppIds
    const conversionFunnelData = useMemo((): ConversionFunnelItem[] => {
        if (filteredEngagements && filteredEngagements.length > 0) {
            if (isPlatformMode) {
                const buildApps = (engagementsForMetric: typeof filteredEngagements) => {
                    const orgCounts = engagementsForMetric.reduce((acc, engagement) => {
                        const organizationKey = engagement.organizationId || 'unknown';
                        acc[organizationKey] = (acc[organizationKey] || 0) + 1;
                        return acc;
                    }, {} as Record<string, number>);

                    return Object.entries(orgCounts).map(([organizationKey, count]) => ({
                        appName: locationNameMap[organizationKey] || organizationKey,
                        count
                    }));
                };

                const orderAttemptEngagements = filteredEngagements.filter(e => e.sessionCategory === 'ACTION_ATTEMPTED');
                const placedOrderEngagements = filteredEngagements.filter(e => e.sessionTags?.includes('ACTION_COMPLETED'));

                return [
                    {
                        id: 'total-calls',
                        label: 'Total Calls',
                        value: filteredEngagements.length,
                        color: funnelColorConfig.totalCalls,
                        apps: buildApps(filteredEngagements)
                    },
                    {
                        id: 'order-attempts',
                        label: 'Order Attempts',
                        value: orderAttemptEngagements.length,
                        color: funnelColorConfig.orderAttempts,
                        apps: buildApps(orderAttemptEngagements)
                    },
                    {
                        id: 'placed-orders',
                        label: 'Placed Orders',
                        value: placedOrderEngagements.length,
                        color: funnelColorConfig.ordersPlaced,
                        apps: buildApps(placedOrderEngagements)
                    }
                ];
            }

            return transformEngagementsToConversionFunnel(filteredEngagements, funnelColorConfig, appNameMap);
        }
        return getEmptyConversionFunnelData(funnelColorConfig);
    }, [filteredEngagements, funnelColorConfig, appNameMap, locationNameMap, isPlatformMode]);

    // Compute transfer language breakdown from engagements (per app, per reason)
    // The transfer reasons API carries no language data, so we derive it from engagement records.
    // Keyed by display name (appNameMap resolved) so it matches app.appName in transfersData
    // without needing an expensive reverse lookup.
    const transferSpanishBreakdown = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return {};

        // { [displayNameOrAppId]: { [reason]: { english, spanish } } }
        const byAppReason: Record<string, Record<string, { english: number; spanish: number }>> = {};

        filteredEngagements.forEach(eng => {
            const appId = eng.applicationId || 'unknown';
            // Key by display name so it matches app.appName from transformSeparateTransferReasonsToChartData
            const appKey = (appNameMap && appNameMap[appId]) || appId;
            const hasPlannedReason = eng.plannedTransferReason && eng.plannedTransferReason.length > 0;
            const hasUnplannedReason = eng.unplannedTransferReason && eng.unplannedTransferReason.length > 0;
            const hasTransferReason = hasPlannedReason || hasUnplannedReason
                || (eng.transferReason && eng.transferReason.length > 0);
            if (!hasTransferReason) return;

            let reason = 'UNKNOWN';
            if (hasPlannedReason) reason = eng.plannedTransferReason;
            else if (hasUnplannedReason) reason = eng.unplannedTransferReason;
            else if (eng.transferReason) reason = eng.transferReason;

            if (reason === 'IMMEDIATE_TRANSFER') return;

            if (!byAppReason[appKey]) byAppReason[appKey] = {};
            if (!byAppReason[appKey][reason]) byAppReason[appKey][reason] = { english: 0, spanish: 0 };

            if (isSpanishEngagement(eng)) {
                byAppReason[appKey][reason].spanish++;
            } else {
                byAppReason[appKey][reason].english++;
            }
        });

        return byAppReason;
    }, [filteredEngagements, appNameMap]);

    // Transfers chart data - filter by selectedAppIds, with language breakdown merged from engagements
    const transfersData = useMemo(() => {
        const plannedTransferReasons = isPlatformMode
            ? filterTransferReasonsByOrganizationIds(
                orgAnalyticsContainer.platformPlannedTransferReasons,
                entityIds
            )
            : filterTransferReasonsByAppIds(
                orgAnalyticsContainer.organizationPlannedTransferReasons,
                selectedAppIds
            );
        const unplannedTransferReasons = isPlatformMode
            ? filterTransferReasonsByOrganizationIds(
                orgAnalyticsContainer.platformUnplannedTransferReasons,
                entityIds
            )
            : filterTransferReasonsByAppIds(
                orgAnalyticsContainer.organizationUnplannedTransferReasons,
                selectedAppIds
            );

        const hasPlannedData = plannedTransferReasons && Object.keys(plannedTransferReasons).length > 0;
        const hasUnplannedData = unplannedTransferReasons && Object.keys(unplannedTransferReasons).length > 0;

        if (hasPlannedData || hasUnplannedData) {
            const chartData = transformSeparateTransferReasonsToChartData(plannedTransferReasons, unplannedTransferReasons, appNameMap);

            // Patch english/spanish onto each app entry using engagement-derived breakdown
            chartData.forEach(category => {
                Object.keys(category).forEach(key => {
                    if (key === 'category') return;
                    const reasonData = (category as any)[key] as { value: number; apps: any[] };
                    if (!reasonData?.apps) return;
                    let apps = reasonData.apps.map(app => {
                        // transferSpanishBreakdown is keyed by display name — matches app.appName directly
                        const langData = transferSpanishBreakdown[app.appName]?.[key];
                        return {
                            ...app,
                            english: langData?.english ?? 0,
                            spanish: langData?.spanish ?? 0
                        };
                    });
                    // When Spanish Only filter is active, keep only apps with Spanish transfers
                    // and recompute the reason total to reflect Spanish-only counts
                    if (isSpanishOnlyFilter) {
                        apps = apps.filter(app => app.spanish > 0);
                        reasonData.value = apps.reduce((sum, app) => sum + app.spanish, 0);
                    }
                    reasonData.apps = apps;
                });
            });

            return chartData;
        }
        return [];
    }, [orgAnalyticsContainer.organizationPlannedTransferReasons, orgAnalyticsContainer.organizationUnplannedTransferReasons, appNameMap, selectedAppIds, transferSpanishBreakdown, isSpanishOnlyFilter]);

    // Compute per-app language breakdown from engagements for small metric tiles
    // Used to enrich API-provided apps arrays with english/spanish counts
    const engagementLangByApp = useMemo(() => {
        if (!filteredEngagements || filteredEngagements.length === 0) return {};
        const byApp: Record<string, { english: number; spanish: number; upsell: number; upsellEnglish: number; upsellSpanish: number; upsellAttemptedEnglish: number; upsellAttemptedSpanish: number; questions: number; questionsEnglish: number; questionsSpanish: number; orders: number; ordersEnglish: number; ordersSpanish: number }> = {};

        filteredEngagements.forEach(eng => {
            const appId = eng.applicationId || 'unknown';
            if (!byApp[appId]) byApp[appId] = { english: 0, spanish: 0, upsell: 0, upsellEnglish: 0, upsellSpanish: 0, upsellAttemptedEnglish: 0, upsellAttemptedSpanish: 0, questions: 0, questionsEnglish: 0, questionsSpanish: 0, orders: 0, ordersEnglish: 0, ordersSpanish: 0 };
            const isSpanish = isSpanishEngagement(eng);
            const tags = eng.sessionTags || [];

            if (isSpanish) byApp[appId].spanish++; else byApp[appId].english++;

            if (tags.includes('UPSELL_ATTEMPTED')) {
                if (isSpanish) byApp[appId].upsellAttemptedSpanish++; else byApp[appId].upsellAttemptedEnglish++;
            }
            if (tags.includes('UPSELL_SUCCESSFUL')) {
                byApp[appId].upsell++;
                if (isSpanish) byApp[appId].upsellSpanish++; else byApp[appId].upsellEnglish++;
            }
            if (eng.sessionCategory === 'QUESTION_ANSWERED') {
                byApp[appId].questions++;
                if (isSpanish) byApp[appId].questionsSpanish++; else byApp[appId].questionsEnglish++;
            }
            if (tags.includes('ACTION_COMPLETED')) {
                const orderCount = tags.filter(t => t === 'ACTION_COMPLETED').length;
                byApp[appId].orders += orderCount;
                if (isSpanish) byApp[appId].ordersSpanish += orderCount; else byApp[appId].ordersEnglish += orderCount;
            }
        });

        return byApp;
    }, [filteredEngagements]);

    // Food ordering metrics - filter by selectedAppIds
    const foodOrderingMetrics = useMemo((): DashboardFoodOrderingMetrics => {
        const perAppMetrics = isPlatformMode
            ? orgAnalyticsContainer.platformTopLevelMetricsPerOrg
            : orgAnalyticsContainer.organizationTopLevelMetricsPerApp;
        const filteredMetrics = aggregateTopLevelMetricsFiltered(
            perAppMetrics,
            isPlatformMode ? entityIds : selectedAppIds,
            isSpanishOnlyFilter
        );

        if (filteredMetrics && (
            filteredMetrics.laborMinutesSaved !== undefined ||
            filteredMetrics.laborHoursSaved !== undefined ||
            filteredMetrics.upsellAttempts !== undefined ||
            filteredMetrics.upsellSuccesses !== undefined ||
            filteredMetrics.averageOrderTotal !== undefined ||
            (filteredMetrics as any).averageTicketSize !== undefined ||
            filteredMetrics.ordersPlaced !== undefined ||
            filteredMetrics.orderAttempts !== undefined ||
            filteredMetrics.totalRevenue !== undefined ||
            filteredMetrics.questionsAnswered !== undefined
        )) {
            return {
                ...transformToFoodOrderingMetrics(filteredMetrics),
                upsoldRevenueGenerated: 0
            };
        }
        return {
            upsellSuccessRate: 0,
            successfulUpsells: 0,
            upsellAttempts: 0,
            averageTicketSize: 0,
            totalSold: 0,
            ordersPlaced: 0,
            questionsAnswered: 0,
            laborHoursSaved: 0,
            upsoldRevenueGenerated: 0,
            upsellApps: [],
            ticketApps: [],
            questionsApps: [],
            laborHoursSavedApps: []
        };
    }, [orgAnalyticsContainer.organizationTopLevelMetricsPerApp, orgAnalyticsContainer.platformTopLevelMetricsPerOrg, selectedAppIds, entityIds, isSpanishOnlyFilter, isPlatformMode]);

    // Aggregate Spanish-only totals for tiles whose API metrics lack Spanish variants.
    // Used when isSpanishOnlyFilter is active to override foodOrderingMetrics values.
    // Per-app arrays are built from engagementLangByApp (derived from filteredEngagements,
    // which is already narrowed to Spanish-only when the filter is active).
    const spanishFoodMetrics = useMemo(() => {
        const perAppMetrics = isPlatformMode
            ? orgAnalyticsContainer.platformTopLevelMetricsPerOrg
            : orgAnalyticsContainer.organizationTopLevelMetricsPerApp;

        // Use the same aggregation pipeline as the combined path, but with spanishOnly=true.
        // This reads the Spanish-specific API fields (laborMinutesSavedSpanish, totalQuestionsAnsweredSpanish,
        // averageTicketSizeSpanish, totalUpsellAttemptsSpanish, totalUpsellsCompletedSpanish, etc.)
        const spanishAggregated = aggregateTopLevelMetricsFiltered(
            perAppMetrics,
            isPlatformMode ? entityIds : selectedAppIds,
            true
        );
        const spanishMetrics = spanishAggregated ? transformToFoodOrderingMetrics(spanishAggregated) : null;

        // Per-app breakdown arrays — API does not return Spanish per-app breakdowns, so these
        // continue to be derived from engagement records via engagementLangByApp.
        const entries = Object.entries(engagementLangByApp);
        const resolveAppName = (appId: string) => (appNameMap && appNameMap[appId]) || appId;

        const upsellApps = entries
            .filter(([, a]) => a.upsellSpanish > 0)
            .map(([appId, a]) => ({
                appName: resolveAppName(appId),
                count: a.upsellSpanish,
                english: 0,
                spanish: a.upsellSpanish,
            }));

        const questionsApps = entries
            .filter(([, a]) => a.questionsSpanish > 0)
            .map(([appId, a]) => ({
                appName: resolveAppName(appId),
                count: a.questionsSpanish,
                english: 0,
                spanish: a.questionsSpanish,
            }));

        const ordersApps = entries
            .filter(([, a]) => a.ordersSpanish > 0)
            .map(([appId, a]) => ({
                appName: resolveAppName(appId),
                count: a.ordersSpanish,
                english: 0,
                spanish: a.ordersSpanish,
            }));

        const laborHoursSavedApps = (spanishMetrics?.laborHoursSavedApps ?? []).map(app => ({
            appName: resolveAppName(app.appName),
            count: app.count,
            english: app.english ?? 0,
            spanish: app.spanish ?? 0,
        }));

        const upsellSuccesses = spanishMetrics?.successfulUpsells ?? 0;
        const upsellAttempted = spanishMetrics?.upsellAttempts ?? 0;

        return {
            upsellSuccesses,
            upsellAttempted,
            upsellSuccessRate: spanishMetrics?.upsellSuccessRate ?? 0,
            questionsAnswered: spanishMetrics?.questionsAnswered ?? 0,
            ordersPlaced: spanishMetrics?.ordersPlaced ?? 0,
            averageTicketSize: spanishMetrics?.averageTicketSize ?? 0,
            totalSold: spanishMetrics?.totalSold ?? 0,
            laborHoursSaved: spanishMetrics?.laborHoursSaved ?? 0,
            upsellApps,
            questionsApps,
            ordersApps,
            laborHoursSavedApps,
        };
    }, [engagementLangByApp, appNameMap, orgAnalyticsContainer.organizationTopLevelMetricsPerApp, orgAnalyticsContainer.platformTopLevelMetricsPerOrg, selectedAppIds, entityIds, isPlatformMode]);

    const transferTotals = useMemo(() => calculateTransferTotals(transfersData), [transfersData]);

    const isPlatformOrderCompletionChartLoading = isChartsLoading ||
        orgAnalyticsContainer.loadingPlatformTopLevelMetrics ||
        orgAnalyticsContainer.loadingPlatformUpsoldRevenue;

    const platformBarChartConfig = useMemo(() => {
        switch (platformBarChartView) {
            case 'placedOrderVolume':
                return {
                    title: 'Placed Order Volume by Organization',
                    metricLabel: 'Placed Orders',
                    metricKey: 'CONTAINED_CALLS_ORDERING',
                    dataKey: 'placedOrders',
                    valueLabel: 'orders',
                    axisFormat: (value: number | string) => Number(value).toLocaleString(),
                    barLabel: (value: number) => Number(value).toLocaleString(),
                    emptyState: 'We could not find any placed orders for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.placedOrders > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => data.placedOrders.toLocaleString(),
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.orderPlaceRate.toFixed(1)}% order place rate from ${data.orderAttempts.toLocaleString()} attempts`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Placed order volume by organization'
                };
            case 'averageTicketSize':
                return {
                    title: 'Average Ticket Size by Organization',
                    metricLabel: 'Average Ticket Size',
                    metricKey: 'AVERAGE_TICKET_SIZE_ORDERING',
                    dataKey: 'averageTicketSize',
                    valueLabel: 'ticket',
                    axisFormat: (value: number | string) => `$${Number(value).toFixed(0)}`,
                    barLabel: (value: number) => `$${Number(value).toFixed(2)}`,
                    emptyState: 'We could not find any placed orders for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.averageTicketSize > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => `$${data.averageTicketSize.toFixed(2)}`,
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} average ticket size`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Average ticket size by organization'
                };
            case 'totalGrossRevenue':
                return {
                    title: 'Gross Revenue by Organization',
                    metricLabel: 'Total Gross Revenue',
                    metricKey: 'TOTAL_GROSS_REVENUE_ORDERING',
                    dataKey: 'totalGrossRevenue',
                    valueLabel: 'revenue',
                    axisFormat: (value: number | string) => `$${Number(value).toLocaleString()}`,
                    barLabel: (value: number) => `$${Math.round(value).toLocaleString()}`,
                    emptyState: 'We could not find any gross revenue for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.totalGrossRevenue > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => `$${Math.round(data.totalGrossRevenue).toLocaleString()}`,
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} gross revenue in the selected range`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Gross revenue by organization'
                };
            case 'questionsAnswered':
                return {
                    title: 'Questions Answered by Organization',
                    metricLabel: 'Questions Answered',
                    metricKey: 'QUESTIONS_ANSWERED_ORDERING',
                    dataKey: 'questionsAnswered',
                    valueLabel: 'questions',
                    axisFormat: (value: number | string) => Number(value).toLocaleString(),
                    barLabel: (value: number) => Math.round(value).toLocaleString(),
                    emptyState: 'We could not find any answered questions for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.questionsAnswered > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => data.questionsAnswered.toLocaleString(),
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} questions answered`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Questions answered by organization'
                };
            case 'laborHoursSaved':
                return {
                    title: 'Labor Hours Saved by Organization',
                    metricLabel: 'Labor Hours Saved',
                    metricKey: 'LABOR_HOURS_SAVED',
                    dataKey: 'laborHoursSaved',
                    valueLabel: 'hours',
                    axisFormat: (value: number | string) => `${Number(value).toFixed(1)}h`,
                    barLabel: (value: number) => `${Number(value).toFixed(1)}h`,
                    emptyState: 'We could not find any labor time savings for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.laborHoursSaved > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => formatHoursAndMinutes(data.laborHoursSaved),
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} labor time saved`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Labor hours saved by organization'
                };
            case 'upsellRevenueGenerated':
                return {
                    title: 'Upsell Revenue Generated by Organization',
                    metricLabel: 'Upsell Revenue Generated',
                    metricKey: 'UPSELL_REVENUE_GENERATED_ORDERING',
                    dataKey: 'upsellRevenueGenerated',
                    valueLabel: 'revenue',
                    axisFormat: (value: number | string) => `$${Number(value).toLocaleString()}`,
                    barLabel: (value: number) => `$${Math.round(value).toLocaleString()}`,
                    emptyState: 'We could not find any upsell-driven revenue for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.upsellRevenueGenerated > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => `$${Math.round(data.upsellRevenueGenerated).toLocaleString()}`,
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} upsell revenue based on average ticket lift x upsold calls`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Upsell revenue generated by organization'
                };
            case 'upsellDollarAmount':
                return {
                    title: 'Upsell Dollar Amount by Organization',
                    metricLabel: 'Upsell Dollar Amount',
                    metricKey: 'UPSELL_DOLLAR_AMOUNT_ORDERING',
                    dataKey: 'upsellDollarAmount',
                    valueLabel: 'ticket lift',
                    axisFormat: (value: number | string) => `$${Number(value).toLocaleString()}`,
                    barLabel: (value: number) => `$${Number(value).toFixed(2)}`,
                    emptyState: 'We could not find any upsell dollar amount data for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.upsellDollarAmount > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => `$${data.upsellDollarAmount.toFixed(2)}`,
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.organizationName} average upsell ticket lift compared to non-upsold orders`,
                    valueScale: { type: 'linear' as const, min: 0 },
                    ariaLabel: 'Upsell dollar amount by organization'
                };
            case 'orderPlaceRate':
            default:
                return {
                    title: 'Order Place Rate by Organization',
                    metricLabel: 'Order Place Rate',
                    metricKey: 'ORDER_COMPLETION_RATE',
                    dataKey: 'orderPlaceRate',
                    valueLabel: 'rate',
                    axisFormat: (value: number | string) => `${value}%`,
                    barLabel: (value: number) => `${Number(value).toFixed(1)}%`,
                    emptyState: 'We could not find any order attempts for the selected organizations in this date range.',
                    hasData: (data: PlatformMetricBarChartDatum[]) => data.some(item => item.orderAttempts > 0),
                    tooltipValue: (data: PlatformMetricBarChartDatum) => `${data.orderPlaceRate.toFixed(1)}%`,
                    tooltipSubtitle: (data: PlatformMetricBarChartDatum) => `${data.placedOrders.toLocaleString()} placed orders out of ${data.orderAttempts.toLocaleString()} attempts`,
                    valueScale: { type: 'linear' as const, min: 0, max: 100 },
                    ariaLabel: 'Order place rate by organization'
                };
        }
    }, [platformBarChartView]);

    const platformMetricBarChartData = useMemo((): PlatformMetricBarChartDatum[] => {
        if (!isPlatformMode) {
            return [];
        }

        const metricsByOrg = orgAnalyticsContainer.platformTopLevelMetricsPerOrg || {};
        const upsoldRevenuePerOrg = orgAnalyticsContainer.platformUpsoldRevenuePerOrg || {};

        return Array.from(entityIds).map((organizationId) => {
            const metrics = metricsByOrg[organizationId] || {};
            const averageTicketSize = Number(
                metrics.averageTicketSize ?? metrics.averageOrderTotal ?? metrics.AverageOrderTotal ?? metrics.totalAverageOrderValue ?? metrics.TotalAverageOrderValue ?? 0
            ) || 0;
            const orderAttempts = Math.max(
                Number((metrics as any).totalOrderAttempts ?? metrics.orderAttempts ?? metrics.containableCalls ?? 0) || 0,
                0
            );
            const placedOrders = Number(metrics.totalOrdersCompleted ?? metrics.ordersPlaced ?? 0) || 0;
            const orderPlaceRate = orderAttempts > 0
                ? Math.round((placedOrders / orderAttempts) * 1000) / 10
                : 0;
            const totalGrossRevenue = Number(metrics.totalRevenue ?? (placedOrders * averageTicketSize)) || 0;
            const questionsAnswered = Number(metrics.totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0) || 0;
            const laborMinutesSaved = Number(metrics.laborMinutesSaved ?? 0) || 0;
            const laborHoursSaved = laborMinutesSaved > 0 ? laborMinutesSaved / 60 : (Number(metrics.laborHoursSaved ?? 0) || 0);
            const upsellSuccesses = Number(metrics.totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0) || 0;
            const averageUpsellTicketDifference = Number(upsoldRevenuePerOrg[organizationId] ?? 0) || 0;
            const upsellDollarAmount = averageUpsellTicketDifference;
            const upsellRevenueGenerated = averageUpsellTicketDifference * upsellSuccesses;

            return {
                organizationId,
                organizationLabel: organizationId,
                organizationName: locationNameMap[organizationId] || organizationId,
                orderPlaceRate,
                orderAttempts,
                placedOrders,
                averageTicketSize,
                totalGrossRevenue,
                questionsAnswered,
                laborHoursSaved,
                upsellDollarAmount,
                upsellRevenueGenerated
            };
        });
    }, [orgAnalyticsContainer.platformTopLevelMetricsPerOrg, orgAnalyticsContainer.platformUpsoldRevenuePerOrg, entityIds, isPlatformMode, locationNameMap]);

    const platformBarChartData = useMemo(() => {
        const sortedData = [...platformMetricBarChartData];
        const dataKey = platformBarChartConfig.dataKey;

        sortedData.sort((a, b) => {
            const primaryDifference = Number(b[dataKey] ?? 0) - Number(a[dataKey] ?? 0);

            if (primaryDifference !== 0) {
                return primaryDifference;
            }

            if (b.placedOrders !== a.placedOrders) {
                return b.placedOrders - a.placedOrders;
            }

            return a.organizationName.localeCompare(b.organizationName);
        });

        return sortedData;
    }, [platformMetricBarChartData, platformBarChartConfig]);

    // Full funnel data with colors (includes Total Calls for calculations)
    const funnelDataWithColors = useMemo(() => conversionFunnelData.map((item, idx) => ({
        ...item,
        color: funnelGlassColors[idx % funnelGlassColors.length],
        borderColor: funnelColors[idx % funnelColors.length]
    })), [conversionFunnelData]);

    // Display funnel data - excludes Total Calls when SHOW_TOTAL_CALLS_IN_FUNNEL is false
    // Colors are re-indexed so the first displayed item gets the first color
    const displayFunnelData = useMemo(() => {
        const filteredData = SHOW_TOTAL_CALLS_IN_FUNNEL
            ? conversionFunnelData
            : conversionFunnelData.filter(item => item.id !== 'total-calls');

        return filteredData.map((item, idx) => ({
            ...item,
            color: funnelGlassColors[idx % funnelGlassColors.length],
            borderColor: funnelColors[idx % funnelColors.length]
        }));
    }, [conversionFunnelData, SHOW_TOTAL_CALLS_IN_FUNNEL]);

    // Export handlers - compute filtered data directly from base records to ensure all data is exported
    const handleExportCSV = useCallback(() => {
        setIsExportingCallData(true);
        // Use setTimeout to yield to UI before heavy computation
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Category', 'All Tags'];
                const tagHeaders = foodOrderingTags.map(tag => getTagName(tag, 'FOOD_ORDERING'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = foodOrderingCallDataRecords;
                if (hasActiveCallFilters) {
                    dataToExport = foodOrderingCallDataRecords.filter(call => {
                        const searchLower = callSearchQuery.toLowerCase();
                        const matchesSearch = !callSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.category.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedCallPhones.size > 0) {
                            matchesPhone = callPhoneFilterMode === 'include'
                                ? selectedCallPhones.has(call.phoneLastFour)
                                : !selectedCallPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedCallLocations.size === 0 || selectedCallLocations.has(call.location);
                        const matchesTags = selectedTags.size === 0 || call.tags.some(tag => selectedTags.has(tag));
                        const matchesCategory = selectedCategories.size === 0 || selectedCategories.has(call.category);

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags && matchesCategory;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, sortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}`,
                        'Category': call.category,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'FOOD_ORDERING')).join('; ')
                    };

                    foodOrderingTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'FOOD_ORDERING');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActiveCallFilters ? '-filtered' : '';
                const filename = `all-calls-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingCallData(false);
            }
        }, 50);
    }, [foodOrderingCallDataRecords, hasActiveCallFilters, callSearchQuery, selectedCallPhones, callPhoneFilterMode, selectedCallLocations, selectedTags, selectedCategories, sortConfig, startDate, endDate, csvHandler]);

    const handleExportTransfersCSV = useCallback(() => {
        setIsExportingTransfers(true);
        setTimeout(() => {
            try {
                const headers = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'Transfer Type', 'Transfer Reason'];

                // Compute filtered data directly from base records
                let dataToExport = foodOrderingTransferDataRecords;
                if (hasActiveTransferFilters) {
                    dataToExport = foodOrderingTransferDataRecords.filter(transfer => {
                        const searchLower = transferSearchQuery.toLowerCase();
                        const matchesSearch = !transferSearchQuery ||
                            transfer.location.toLowerCase().includes(searchLower) ||
                            transfer.phoneLastFour.toLowerCase().includes(searchLower) ||
                            transfer.date.toLowerCase().includes(searchLower) ||
                            transfer.time.toLowerCase().includes(searchLower) ||
                            transfer.transferType.toLowerCase().includes(searchLower) ||
                            (TRANSFER_REASON_LABELS[transfer.transferReason] || transfer.transferReason).toLowerCase().includes(searchLower);

                        let matchesPhone = true;
                        if (selectedTransferPhones.size > 0) {
                            matchesPhone = transferPhoneFilterMode === 'include'
                                ? selectedTransferPhones.has(transfer.phoneLastFour)
                                : !selectedTransferPhones.has(transfer.phoneLastFour);
                        }

                        const matchesLocation = selectedTransferLocations.size === 0 || selectedTransferLocations.has(transfer.location);
                        const matchesType = selectedTransferType === 'all' || transfer.transferType === selectedTransferType;
                        const matchesReason = selectedTransferReasons.size === 0 || selectedTransferReasons.has(transfer.transferReason);

                        return matchesSearch && matchesPhone && matchesLocation && matchesType && matchesReason;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, transferSortConfig, true);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(transfer => ({
                    'Date': transfer.date,
                    'Time': transfer.time,
                    'Phone (Last 4)': `***${transfer.phoneLastFour}`,
                    'Location': transfer.location,
                    'Transcript Link': `${baseUrl}/v/apps/${transfer.applicationId}/analytics/sessions?sessionId=${transfer.sessionId}`,
                    'Transfer Type': transfer.transferType,
                    'Transfer Reason': (TRANSFER_REASON_LABELS[transfer.transferReason] ?? transfer.transferReason) || 'Undetermined'
                }));

                const filterSuffix = hasActiveTransferFilters ? '-filtered' : '';
                const filename = `transfers-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingTransfers(false);
            }
        }, 50);
    }, [foodOrderingTransferDataRecords, hasActiveTransferFilters, transferSearchQuery, selectedTransferPhones, transferPhoneFilterMode, selectedTransferLocations, selectedTransferType, selectedTransferReasons, transferSortConfig, startDate, endDate, csvHandler]);

    const handleExportOrderAttemptsCSV = useCallback(() => {
        setIsExportingOrderAttempts(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'All Tags'];
                const tagHeaders = foodOrderingTags.map(tag => getTagName(tag, 'FOOD_ORDERING'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = orderAttemptsBaseRecords;
                if (hasActiveOrderAttemptsFilters) {
                    dataToExport = orderAttemptsBaseRecords.filter(call => {
                        const searchLower = orderAttemptsSearchQuery.toLowerCase();
                        const matchesSearch = !orderAttemptsSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedOrderAttemptsPhones.size > 0) {
                            matchesPhone = orderAttemptsPhoneFilterMode === 'include'
                                ? selectedOrderAttemptsPhones.has(call.phoneLastFour)
                                : !selectedOrderAttemptsPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedOrderAttemptsLocations.size === 0 || selectedOrderAttemptsLocations.has(call.location);
                        const matchesTags = selectedOrderAttemptsTags.size === 0 || call.tags.some(tag => selectedOrderAttemptsTags.has(tag));

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, orderAttemptsSortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}`,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'FOOD_ORDERING')).join('; ')
                    };

                    foodOrderingTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'FOOD_ORDERING');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActiveOrderAttemptsFilters ? '-filtered' : '';
                const filename = `order-attempts-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingOrderAttempts(false);
            }
        }, 50);
    }, [orderAttemptsBaseRecords, hasActiveOrderAttemptsFilters, orderAttemptsSearchQuery, selectedOrderAttemptsPhones, orderAttemptsPhoneFilterMode, selectedOrderAttemptsLocations, selectedOrderAttemptsTags, orderAttemptsSortConfig, startDate, endDate, csvHandler]);

    const handleExportPlacedOrdersCSV = useCallback(() => {
        setIsExportingPlacedOrders(true);
        setTimeout(() => {
            try {
                const baseHeaders = ['Date', 'Time', 'Phone (Last 4)', 'Location', 'Transcript Link', 'All Tags'];
                const tagHeaders = foodOrderingTags.map(tag => getTagName(tag, 'FOOD_ORDERING'));
                const headers = [...baseHeaders, ...tagHeaders];

                // Compute filtered data directly from base records
                let dataToExport = placedOrdersBaseRecords;
                if (hasActivePlacedOrdersFilters) {
                    dataToExport = placedOrdersBaseRecords.filter(call => {
                        const searchLower = placedOrdersSearchQuery.toLowerCase();
                        const matchesSearch = !placedOrdersSearchQuery ||
                            call.location.toLowerCase().includes(searchLower) ||
                            call.phoneLastFour.toLowerCase().includes(searchLower) ||
                            call.date.toLowerCase().includes(searchLower) ||
                            call.time.toLowerCase().includes(searchLower) ||
                            call.tags.some(tag => getTagName(tag, 'FOOD_ORDERING').toLowerCase().includes(searchLower));

                        let matchesPhone = true;
                        if (selectedPlacedOrdersPhones.size > 0) {
                            matchesPhone = placedOrdersPhoneFilterMode === 'include'
                                ? selectedPlacedOrdersPhones.has(call.phoneLastFour)
                                : !selectedPlacedOrdersPhones.has(call.phoneLastFour);
                        }

                        const matchesLocation = selectedPlacedOrdersLocations.size === 0 || selectedPlacedOrdersLocations.has(call.location);
                        const matchesTags = selectedPlacedOrdersTags.size === 0 || call.tags.some(tag => selectedPlacedOrdersTags.has(tag));

                        return matchesSearch && matchesPhone && matchesLocation && matchesTags;
                    });
                }
                dataToExport = sortDataRecords(dataToExport, placedOrdersSortConfig);

                const baseUrl = getSparkBaseUrl() || window.location.origin;
                const exportData = dataToExport.map(call => {
                    const baseData: Record<string, string> = {
                        'Date': call.date,
                        'Time': call.time,
                        'Phone (Last 4)': `***${call.phoneLastFour}`,
                        'Location': call.location,
                        'Transcript Link': `${baseUrl}/v/apps/${call.applicationId}/analytics/sessions?sessionId=${call.sessionId}`,
                        'All Tags': call.tags.map(tag => getTagName(tag, 'FOOD_ORDERING')).join('; ')
                    };

                    foodOrderingTags.forEach(tag => {
                        const tagDisplayName = getTagName(tag, 'FOOD_ORDERING');
                        baseData[tagDisplayName] = call.tags.includes(tag) ? 'Yes' : '';
                    });

                    return baseData;
                });

                const filterSuffix = hasActivePlacedOrdersFilters ? '-filtered' : '';
                const filename = `placed-orders-${moment(startDate).format('YYYY-MM-DD')}-to-${moment(endDate).format('YYYY-MM-DD')}${filterSuffix}`;
                csvHandler.exportCSV(exportData, headers, filename);
            } finally {
                setIsExportingPlacedOrders(false);
            }
        }, 50);
    }, [placedOrdersBaseRecords, hasActivePlacedOrdersFilters, placedOrdersSearchQuery, selectedPlacedOrdersPhones, placedOrdersPhoneFilterMode, selectedPlacedOrdersLocations, selectedPlacedOrdersTags, placedOrdersSortConfig, startDate, endDate, csvHandler]);

    return (
        <DashboardGrid>
            {/* Order Conversion */}
            <ChartTile>
                <TileHeader style={{ justifyContent: 'space-between' }}>
                    <TileHeaderButtonWrapper>
                        <TileTitle>Order Conversion</TileTitle>
                        <ViewDetailsButton
                            onClick={() => setIsCallDataModalOpen(true)}
                            data-view-export-button
                            disabled={isButtonsDisabled}
                        >
                            View Details
                        </ViewDetailsButton>
                    </TileHeaderButtonWrapper>
                    {(() => {
                        const orderAttemptsValue = conversionFunnelData.find(d => d.id === 'order-attempts')?.value ?? 0;
                        const placedOrdersValue = conversionFunnelData.find(d => d.id === 'placed-orders')?.value ?? 0;
                        const rate = orderAttemptsValue > 0 ? Math.ceil((placedOrdersValue / orderAttemptsValue) * 100) : 0;
                        return !isFunnelLoading && orderAttemptsValue > 0 ? (
                            <TileHeaderStatsSection>
                                <TileHeaderNumber>
                                    {rate}%
                                </TileHeaderNumber>
                                <TileHeaderLabel>
                                    Order Place Rate
                                    <MetricInfoTooltip metricKey="ORDER_COMPLETION_RATE" iconSize={12} />
                                </TileHeaderLabel>
                            </TileHeaderStatsSection>
                        ) : null;
                    })()}
                </TileHeader>
                <FunnelChartContainer>
                    {(() => {
                        const orderAttemptsValue = conversionFunnelData.find(d => d.id === 'order-attempts')?.value ?? 0;
                        const placedOrdersValue = conversionFunnelData.find(d => d.id === 'placed-orders')?.value ?? 0;
                        return (
                            <>
                                {isFunnelLoading && (
                                    <ChartLoadingOverlay>
                                        <SecondaryLoader className="grey" />
                                    </ChartLoadingOverlay>
                                )}
                                {!isFunnelLoading && orderAttemptsValue === 0 && (
                                    <EmptyStateMessage>
                                        {isTodayDateRange
                                            ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                                            : "We couldn't find any order conversion data for the selected date range and assistants."}
                                    </EmptyStateMessage>
                                )}
                                {orderAttemptsValue > 0 && (
                                    <>
                                        <ResponsiveFunnel
                                            data={displayFunnelData as any}
                                            margin={isMobile ? { top: 10, right: 10, bottom: 10, left: 10 } : { top: 20, right: 20, bottom: 20, left: 20 }}
                                            colors={displayFunnelData.map(d => d.color)}
                                            borderWidth={funnelChartDefaults.borderWidth}
                                            borderColor={(part) => {
                                                const idx = displayFunnelData.findIndex(d => d.id === part.data.id);
                                                return displayFunnelData[idx >= 0 ? idx : 0]?.borderColor || funnelColors[0];
                                            }}
                                            labelColor={{ from: 'color', modifiers: [['darker', 3]] }}
                                            beforeSeparatorLength={funnelChartDefaults.beforeSeparatorLength}
                                            beforeSeparatorOffset={funnelChartDefaults.beforeSeparatorOffset}
                                            afterSeparatorLength={funnelChartDefaults.afterSeparatorLength}
                                            afterSeparatorOffset={funnelChartDefaults.afterSeparatorOffset}
                                            currentPartSizeExtension={funnelChartDefaults.currentPartSizeExtension}
                                            currentBorderWidth={funnelChartDefaults.currentBorderWidth}
                                            valueFormat={(value) => value.toLocaleString()}
                                            theme={funnelTheme}
                                            motionConfig={funnelChartDefaults.motionConfig}
                                            onClick={(part) => {
                                                // Guard: don't open modals if interactive features aren't ready or no data
                                                if (!isInteractiveFeaturesReady || foodOrderingCallDataRecords.length === 0) {
                                                    return;
                                                }
                                                const data = part.data as any;
                                                // Open the dedicated modal for order-attempts or placed-orders
                                                if (data.id === 'order-attempts') {
                                                    setIsOrderAttemptsModalOpen(true);
                                                } else if (data.id === 'placed-orders') {
                                                    setIsPlacedOrdersModalOpen(true);
                                                } else {
                                                    // Fallback to ChartDetailModal for other funnel segments
                                                    setChartDetailTitle(data.label);
                                                    setChartDetailValueLabel('conversations');
                                                    setChartDetailCountPrefix('');
                                                    setChartDetailShowPercentOfTotal(true);
                                                    setChartDetailSummaryLabel(data.label);
                                                    setSelectedFunnelData([{
                                                        title: data.label,
                                                        value: data.value,
                                                        apps: data.apps || []
                                                    }]);
                                                    setChartDetailTranscripts([]);
                                                    setShowTranscriptsTab(false);
                                                    setIsChartDetailModalOpen(true);
                                                }
                                            }}
                                            tooltip={({ part }) => {
                                                const data = part.data as any;
                                                const idx = displayFunnelData.findIndex(d => d.id === data.id);
                                                return (
                                                    <ChartTooltip
                                                        title={data.label}
                                                        value={data.value}
                                                        subtitle={data.id === 'placed-orders' ? `${orderAttemptsValue > 0 ? Math.ceil((data.value / orderAttemptsValue) * 100) : '0'}% of order attempts` : undefined}
                                                        apps={data.apps || []}
                                                        color={displayFunnelData[idx >= 0 ? idx : 0]?.color || funnelGlassColors[0]}
                                                        borderColor={displayFunnelData[idx >= 0 ? idx : 0]?.borderColor || funnelColors[0]}
                                                        showLanguageBreakdown={isMultiLingual}
                                                        spanishOnly={isSpanishOnlyFilter}
                                                        clickHint={true}
                                                        compact={isMobile}
                                                    />
                                                );
                                            }}
                                        />
                                    </>
                                )}
                            </>
                        );
                    })()}
                </FunnelChartContainer>
            </ChartTile>

            {/* Transfers */}
            <TallChartTile>
                {isPlatformMode ? (
                    <>
                        <TileHeader style={{ justifyContent: 'space-between' }}>
                            <TileHeaderButtonWrapper>
                                <TileTitle>{platformBarChartConfig.title}</TileTitle>
                                <div style={{ position: 'relative' }}>
                                    <select
                                        value={platformBarChartView}
                                        onChange={(event) => setPlatformBarChartView(event.target.value as PlatformBarChartView)}
                                        disabled={isPlatformOrderCompletionChartLoading}
                                        style={{
                                            fontSize: 12,
                                            fontWeight: 600,
                                            color: text_dark,
                                            padding: '4px 22px 4px 8px',
                                            border: '1px solid #E9EAEE',
                                            borderRadius: 6,
                                            background: 'white',
                                            cursor: isPlatformOrderCompletionChartLoading ? 'not-allowed' : 'pointer',
                                            appearance: 'none',
                                            fontFamily: 'Muli',
                                            opacity: isPlatformOrderCompletionChartLoading ? 0.7 : 1
                                        }}
                                    >
                                        <option value="orderPlaceRate">Order Place Rate</option>
                                        <option value="placedOrderVolume">Placed Order Volume</option>
                                        <option value="averageTicketSize">Average Ticket Size</option>
                                        <option value="totalGrossRevenue">Gross Revenue</option>
                                        <option value="questionsAnswered">Questions Answered</option>
                                        <option value="laborHoursSaved">Labor Hours Saved</option>
                                        <option value="upsellDollarAmount">Upsell Dollar Amount</option>
                                        <option value="upsellRevenueGenerated">Upsell Revenue Generated</option>
                                    </select>
                                    <span style={{
                                        position: 'absolute',
                                        right: 8,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        pointerEvents: 'none',
                                        fontSize: 10,
                                        color: color_shades_darker
                                    }}>
                                        ▼
                                    </span>
                                </div>
                            </TileHeaderButtonWrapper>
                            {!isPlatformOrderCompletionChartLoading && platformBarChartData.length > 0 && (
                                <TileHeaderStatsSection>
                                    <TileHeaderNumber>
                                        {platformBarChartData.length}
                                    </TileHeaderNumber>
                                    <TileHeaderLabel>
                                        Selected Orgs
                                        <MetricInfoTooltip metricKey={platformBarChartConfig.metricKey} iconSize={11} />
                                    </TileHeaderLabel>
                                </TileHeaderStatsSection>
                            )}
                        </TileHeader>
                        <TallChartContainer>
                            {isPlatformOrderCompletionChartLoading && (
                                <ChartLoadingOverlay>
                                    <SecondaryLoader className="grey" />
                                </ChartLoadingOverlay>
                            )}
                            {!isPlatformOrderCompletionChartLoading && platformBarChartData.length === 0 && (
                                <EmptyStateMessage>
                                    Select at least one organization to see this chart.
                                </EmptyStateMessage>
                            )}
                            {!isPlatformOrderCompletionChartLoading && platformBarChartData.length > 0 && !platformBarChartConfig.hasData(platformBarChartData) && (
                                <EmptyStateMessage>
                                    {platformBarChartConfig.emptyState}
                                </EmptyStateMessage>
                            )}
                            {platformBarChartData.length > 0 && platformBarChartConfig.hasData(platformBarChartData) && (
                                <ResponsiveBar
                                    data={platformBarChartData}
                                    keys={[platformBarChartConfig.dataKey]}
                                    indexBy="organizationLabel"
                                    layout="horizontal"
                                    margin={{ top: 12, right: 24, bottom: 36, left: 128 }}
                                    padding={barChartDefaults.padding}
                                    innerPadding={barChartDefaults.innerPadding}
                                    valueScale={platformBarChartConfig.valueScale}
                                    indexScale={{ type: 'band', round: true }}
                                    colors={voicify_spark_dark}
                                    borderColor={voicify_spark_darker}
                                    borderWidth={barChartDefaults.borderWidth}
                                    borderRadius={barChartDefaults.borderRadius}
                                    enableGridX={barChartDefaults.enableGridX}
                                    enableGridY={barChartDefaults.enableGridY}
                                    enableLabel={true}
                                    labelSkipWidth={barChartDefaults.labelSkipWidth}
                                    labelSkipHeight={barChartDefaults.labelSkipHeight}
                                    labelTextColor={color_shades_darkest}
                                    label={(bar) => platformBarChartConfig.barLabel(Number(bar.value))}
                                    theme={nivoChartTheme}
                                    animate={barChartDefaults.animate}
                                    motionConfig={barChartDefaults.motionConfig}
                                    axisTop={null}
                                    axisRight={null}
                                    axisBottom={{
                                        tickSize: 0,
                                        tickPadding: 10,
                                        tickRotation: 0,
                                        format: (value) => platformBarChartConfig.axisFormat(value),
                                        legend: platformBarChartConfig.metricLabel,
                                        legendPosition: 'middle',
                                        legendOffset: 30
                                    }}
                                    axisLeft={{
                                        tickSize: 0,
                                        tickPadding: 12,
                                        tickRotation: 0,
                                        format: (value) => {
                                            const matchingOrg = platformBarChartData.find(item => item.organizationLabel === value);
                                            const label = matchingOrg?.organizationName || String(value);
                                            return label.length > 20 ? `${label.slice(0, 20)}...` : label;
                                        }
                                    }}
                                    tooltip={({ data, value, color }) => (
                                        <ChartTooltip
                                            title={data.organizationName as string}
                                            value={platformBarChartConfig.tooltipValue(data as PlatformMetricBarChartDatum)}
                                            subtitle={platformBarChartConfig.tooltipSubtitle(data as PlatformMetricBarChartDatum)}
                                            valueLabel={platformBarChartConfig.valueLabel}
                                            color={color}
                                            borderColor={voicify_spark_darker}
                                            maxWidth="260px"
                                        />
                                    )}
                                    role="application"
                                    ariaLabel={platformBarChartConfig.ariaLabel}
                                />
                            )}
                        </TallChartContainer>
                    </>
                ) : (
                    <>
                        <TileHeader style={{ justifyContent: 'space-between' }}>
                            <TileHeaderButtonWrapper>
                                <TileTitle>Transfers</TileTitle>
                                <ViewDetailsButton
                                    onClick={() => setIsTransfersModalOpen(true)}
                                    data-view-export-button
                                    disabled={isButtonsDisabled}
                                >
                                    View Details
                                </ViewDetailsButton>
                            </TileHeaderButtonWrapper>
                            {!isTransfersLoading && transfersData.length > 0 && (
                                <TileHeaderStatsSection>
                                    <TileHeaderNumber>
                                        {transferTotals.total.toLocaleString()}
                                    </TileHeaderNumber>
                                    <TileHeaderLabel>
                                        Excludes immediate transfers
                                    </TileHeaderLabel>
                                </TileHeaderStatsSection>
                            )}
                        </TileHeader>
                        <TallChartContainer>
                            {isTransfersLoading && (
                                <ChartLoadingOverlay>
                                    <SecondaryLoader className="grey" />
                                </ChartLoadingOverlay>
                            )}
                            {!isTransfersLoading && (transfersData.length === 0 || transferTotals.total === 0) && (
                                <EmptyStateMessage>
                                    {isTodayDateRange
                                        ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                                        : "We couldn't find any transfer data for the selected date range and assistants."}
                                </EmptyStateMessage>
                            )}
                            {transfersData.length > 0 && transferTotals.total > 0 && (
                                <TransfersChart
                                    data={transfersData}
                                    height="100%"
                                    margin={{ top: 10, right: 20, bottom: 80, left: 40 }}
                                    showTotals={false}
                                    showLegend={false}
                                    chartLayout="vertical-by-reason"
                                    showCategoryLegend={true}
                                    showLanguageBreakdown={isMultiLingual}
                                    spanishOnly={isSpanishOnlyFilter}
                                    renderLegendTooltip={(category) => {
                                        if (category === 'Planned') return <MetricInfoTooltip metricKey="PLANNED_TRANSFERS_ORDERING" iconSize={11} showCalculation={false} />;
                                        if (category === 'Unplanned') return <MetricInfoTooltip metricKey="UNPLANNED_TRANSFERS_ORDERING" iconSize={11} showCalculation={false} />;
                                        if (category === 'Undetermined') return <MetricInfoTooltip metricKey="UNDETERMINED_TRANSFERS_ORDERING" iconSize={11} showCalculation={false} />;
                                        return null;
                                    }}
                                    onBarClick={(data) => {
                                        setChartDetailTitle(data.reason);
                                        setChartDetailValueLabel('transfers');
                                        setChartDetailCountPrefix('');
                                        setChartDetailShowPercentOfTotal(true);
                                        setChartDetailSummaryLabel(data.reason);
                                        setSelectedFunnelData([{
                                            title: data.reason,
                                            value: data.value,
                                            subtitle: `${data.category} Transfer`,
                                            apps: data.apps.map(app => ({
                                                appName: app.appName,
                                                count: app.count,
                                                english: app.english,
                                                spanish: app.spanish
                                            }))
                                        }]);
                                        // Filter transfer records by the clicked reason and convert to transcript format
                                        const filteredTransfers = foodOrderingTransferDataRecords.filter(t => t.transferReason === data.reasonKey);
                                        setChartDetailTranscripts(transferRecordsToTranscripts(filteredTransfers, (t) => `${data.category} - ${data.reason}`));
                                        setShowTranscriptsTab(true);
                                        setIsChartDetailModalOpen(true);
                                    }}
                                />
                            )}
                        </TallChartContainer>
                    </>
                )}
            </TallChartTile>

            {/* Small Number Tiles Row */}
            <SmallTilesRow style={platformSmallTilesRowStyle}>
                {/* Upsell Success Rate */}
                <SmallNumberTile
                    onMouseEnter={() => setHoveredTile('upsell')}
                    onMouseLeave={() => setHoveredTile(null)}
                    onClick={() => {
                        if (!isMetricTilesLoading) {
                            setChartDetailTitle('Upsell Success Rate');
                            setChartDetailValueLabel('successful upsells');
                            setChartDetailCountPrefix('');
                            setChartDetailShowPercentOfTotal(true);
                            setChartDetailSummaryLabel('Successful Upsells');
                            setSelectedFunnelData([{
                                title: 'Upsell Success Rate',
                                value: isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccesses : foodOrderingMetrics.successfulUpsells,
                                subtitle: `${Math.ceil(isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccessRate : foodOrderingMetrics.upsellSuccessRate)}% success rate (${isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccesses : foodOrderingMetrics.successfulUpsells} of ${isSpanishOnlyFilter ? spanishFoodMetrics.upsellAttempted : foodOrderingMetrics.upsellAttempts} attempts)`,
                                apps: isSpanishOnlyFilter ? spanishFoodMetrics.upsellApps : foodOrderingMetrics.upsellApps.map(app => ({
                                    appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                    count: app.count,
                                    english: app.english,
                                    spanish: app.spanish
                                }))
                            }]);
                            // Filter for calls with successful upsells
                            const upsellRecords = foodOrderingCallDataRecords.filter(r => r.tags.includes('UPSELL_SUCCESSFUL'));
                            setChartDetailTranscripts(callRecordsToTranscripts(upsellRecords, 'Successful Upsell'));
                            setShowTranscriptsTab(true);
                            setChartDetailHeaderInfo(`${(isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccesses : foodOrderingMetrics.successfulUpsells).toLocaleString()} successful upsells`);
                            setIsChartDetailModalOpen(true);
                        }
                    }}
                    style={smallTileStyle}
                >
                    <TileHeader style={{ justifyContent: 'center' }}>
                        <TileTitle small style={compactTileTitleStyle}>Upsell Success Rate</TileTitle>
                    </TileHeader>
                    {isMetricTilesLoading ? (
                        <NumberLoadingWrapper>
                            <SecondaryLoader className="grey" />
                        </NumberLoadingWrapper>
                    ) : (
                        <NumberDisplay>
                            <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>{Math.ceil(isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccessRate : foodOrderingMetrics.upsellSuccessRate)}%</BigNumber>
                            <NumberSubtext style={compactSubtextStyle}>{isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccesses : foodOrderingMetrics.successfulUpsells} successful upsells</NumberSubtext>
                        </NumberDisplay>
                    )}
                    {hoveredTile === 'upsell' && !isMetricTilesLoading && isInteractiveFeaturesReady && (
                        <ChartTooltip
                            title="Upsell Success Rate"
                            value={`${Math.ceil(isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccessRate : foodOrderingMetrics.upsellSuccessRate)}%`}
                            subtitle={`${isSpanishOnlyFilter ? spanishFoodMetrics.upsellSuccesses : foodOrderingMetrics.successfulUpsells} of ${isSpanishOnlyFilter ? spanishFoodMetrics.upsellAttempted : foodOrderingMetrics.upsellAttempts} attempts`}
                            apps={isSpanishOnlyFilter ? spanishFoodMetrics.upsellApps : foodOrderingMetrics.upsellApps.map(app => {
                                const appId = Object.keys(appNameMap || {}).find(id => (appNameMap?.[id] || id) === app.appName) || app.appName;
                                const lang = engagementLangByApp[appId];
                                return { ...app, appName: (appNameMap && appNameMap[app.appName]) || app.appName, english: lang?.upsellEnglish ?? app.english, spanish: lang?.upsellSpanish ?? app.spanish };
                            })}
                            valueLabel="success rate"
                            showLanguageBreakdown={isMultiLingual}
                            spanishOnly={isSpanishOnlyFilter}
                            clickHint={true}
                            compact={isMobile}
                            overlay={isMobile}
                        />
                    )}
                </SmallNumberTile>

                {/* Sales Summary */}
                <SmallNumberTile
                    onMouseEnter={() => setHoveredTile('sales')}
                    onMouseLeave={() => setHoveredTile(null)}
                    onClick={() => {
                        if (!isMetricTilesLoading) {
                            setChartDetailTitle('Average Ticket Size');
                            setChartDetailValueLabel('avg ticket');
                            setChartDetailCountPrefix('$');
                            setChartDetailShowPercentOfTotal(false);
                            setChartDetailSummaryLabel('Average Ticket Size');
                            setSelectedFunnelData([{
                                title: 'Average Ticket Size',
                                value: isSpanishOnlyFilter ? spanishFoodMetrics.averageTicketSize : foodOrderingMetrics.averageTicketSize,
                                subtitle: `Based on ${conversionFunnelData.find(d => d.id === 'placed-orders')?.value ?? 0} orders`,
                                apps: foodOrderingMetrics.ticketApps.map(app => ({
                                    appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                    count: parseFloat(app.count.toFixed(2)),
                                    english: app.english !== undefined ? parseFloat(app.english.toFixed(2)) : undefined,
                                    spanish: app.spanish !== undefined ? parseFloat(app.spanish.toFixed(2)) : undefined
                                }))
                            }]);
                            // Filter for placed orders (isContained flag - matches funnel chart)
                            const placedOrderRecords = foodOrderingCallDataRecords.filter(r => r.isContained);
                            setChartDetailTranscripts(callRecordsToTranscripts(placedOrderRecords, 'Placed Order'));
                            setShowTranscriptsTab(true);
                            setChartDetailHeaderInfo(`${(conversionFunnelData.find(d => d.id === 'placed-orders')?.value ?? 0).toLocaleString()} placed orders`);
                            setIsChartDetailModalOpen(true);
                        }
                    }}
                    style={smallTileStyle}
                >
                    <TileHeader style={{ justifyContent: 'center' }}>
                        <TileTitle small style={compactTileTitleStyle}>Sales Summary</TileTitle>
                    </TileHeader>
                    {isMetricTilesLoading ? (
                        <NumberLoadingWrapper>
                            <SecondaryLoader className="grey" />
                        </NumberLoadingWrapper>
                    ) : (
                        <NumberDisplay style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: isPlatformMode ? '2px' : '4px', textAlign: 'center' }}>
                                <div>
                                    <SalesSummaryNumber style={compactSalesSummaryValueStyle}>{isSpanishOnlyFilter ? `$${Math.round(spanishFoodMetrics.totalSold).toLocaleString()}` : `$${Math.round(foodOrderingMetrics.totalSold).toLocaleString()}`}</SalesSummaryNumber>
                                </div>
                                <div style={compactSalesSummaryLabelStyle}>{isSpanishOnlyFilter ? 'total sold' : 'total sold'}</div>
                                <div>
                                    <SalesSummaryNumber style={compactSalesSummaryValueStyle}>${(isSpanishOnlyFilter ? spanishFoodMetrics.averageTicketSize : foodOrderingMetrics.averageTicketSize).toFixed(2)}</SalesSummaryNumber>
                                </div>
                                <div style={compactSalesSummaryLabelStyle}>avg ticket</div>
                            </div>
                        </NumberDisplay>
                    )}
                    {hoveredTile === 'sales' && !isMetricTilesLoading && isInteractiveFeaturesReady && (
                        <ChartTooltip
                            title="Average Ticket Size"
                            value={`$${(isSpanishOnlyFilter ? spanishFoodMetrics.averageTicketSize : foodOrderingMetrics.averageTicketSize).toFixed(2)}`}
                            subtitle={`Based on ${conversionFunnelData.find(d => d.id === 'placed-orders')?.value ?? 0} orders`}
                            apps={foodOrderingMetrics.ticketApps.map(app => {
                                const appId = Object.keys(appNameMap || {}).find(id => (appNameMap?.[id] || id) === app.appName) || app.appName;
                                const lang = engagementLangByApp[appId];
                                return { ...app, appName: (appNameMap && appNameMap[app.appName]) || app.appName, count: parseFloat(app.count.toFixed(2)), english: lang?.ordersEnglish ?? app.english, spanish: lang?.ordersSpanish ?? app.spanish };
                            })}
                            valueLabel="avg ticket"
                            countPrefix="$"
                            showLanguageBreakdown={isMultiLingual}
                            spanishOnly={isSpanishOnlyFilter}
                            clickHint={true}
                            compact={isMobile}
                            overlay={isMobile}
                        />
                    )}
                </SmallNumberTile>

                {/* Questions Answered */}
                <SmallNumberTile
                    onMouseEnter={() => setHoveredTile('questions')}
                    onMouseLeave={() => setHoveredTile(null)}
                    onClick={() => {
                        if (!isMetricTilesLoading) {
                            setChartDetailTitle('Questions Answered');
                            setChartDetailValueLabel('questions');
                            setChartDetailCountPrefix('');
                            setChartDetailShowPercentOfTotal(true);
                            setChartDetailSummaryLabel('Questions Answered');
                            setSelectedFunnelData([{
                                title: 'Questions Answered',
                                value: isSpanishOnlyFilter ? spanishFoodMetrics.questionsAnswered : foodOrderingMetrics.questionsAnswered,
                                apps: isSpanishOnlyFilter ? spanishFoodMetrics.questionsApps : foodOrderingMetrics.questionsApps.map(app => ({
                                    appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                    count: app.count,
                                    english: app.english,
                                    spanish: app.spanish
                                }))
                            }]);
                            const questionRecords = foodOrderingCallDataRecords.filter(r => r.category === 'QUESTION_ANSWERED');
                            setChartDetailTranscripts(callRecordsToTranscripts(questionRecords, 'Question Answered'));
                            setShowTranscriptsTab(true);
                            setChartDetailHeaderInfo(`${(isSpanishOnlyFilter ? spanishFoodMetrics.questionsAnswered : foodOrderingMetrics.questionsAnswered).toLocaleString()} questions answered`);
                            setIsChartDetailModalOpen(true);
                        }
                    }}
                    style={smallTileStyle}
                >
                    <TileHeader style={{ justifyContent: 'center' }}>
                        <TileTitle small style={compactTileTitleStyle}>Questions Answered</TileTitle>
                    </TileHeader>
                    {isMetricTilesLoading ? (
                        <NumberLoadingWrapper>
                            <SecondaryLoader className="grey" />
                        </NumberLoadingWrapper>
                    ) : (
                        <NumberDisplay>
                            <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>{(isSpanishOnlyFilter ? spanishFoodMetrics.questionsAnswered : foodOrderingMetrics.questionsAnswered).toLocaleString()}</BigNumber>
                            <NumberSubtext style={compactSubtextStyle}>non-order questions</NumberSubtext>
                        </NumberDisplay>
                    )}
                    {hoveredTile === 'questions' && !isMetricTilesLoading && isInteractiveFeaturesReady && (
                        <ChartTooltip
                            title="Questions Answered"
                            value={isSpanishOnlyFilter ? spanishFoodMetrics.questionsAnswered : foodOrderingMetrics.questionsAnswered}
                            apps={isSpanishOnlyFilter ? spanishFoodMetrics.questionsApps : foodOrderingMetrics.questionsApps.map(app => {
                                const appId = Object.keys(appNameMap || {}).find(id => (appNameMap?.[id] || id) === app.appName) || app.appName;
                                const lang = engagementLangByApp[appId];
                                return { ...app, appName: (appNameMap && appNameMap[app.appName]) || app.appName, english: lang?.questionsEnglish ?? app.english, spanish: lang?.questionsSpanish ?? app.spanish };
                            })}
                            valueLabel="questions"
                            showLanguageBreakdown={isMultiLingual}
                            spanishOnly={isSpanishOnlyFilter}
                            clickHint={true}
                            compact={isMobile}
                            overlay={isMobile}
                        />
                    )}
                </SmallNumberTile>

                {isPlatformMode && (
                    <SmallNumberTile
                        onMouseEnter={() => setHoveredTile('orderAttemptRate')}
                        onMouseLeave={() => setHoveredTile(null)}
                        onClick={() => {
                            if (!isMetricTilesLoading) {
                                setChartDetailTitle('Order Attempt Rate');
                                setChartDetailValueLabel('order attempts');
                                setChartDetailCountPrefix('');
                                setChartDetailShowPercentOfTotal(true);
                                setChartDetailSummaryLabel('Order Attempt Conversations');
                                setSelectedFunnelData([{
                                    title: 'Order Attempts',
                                    value: orderAttemptRateMetrics.orderAttemptCalls,
                                    subtitle: `${orderAttemptRateMetrics.orderAttemptRate.toFixed(1)}% of ${orderAttemptRateMetrics.totalCalls.toLocaleString()} total calls`,
                                    apps: orderAttemptRateMetrics.apps
                                }]);
                                setChartDetailTranscripts(callRecordsToTranscripts(orderAttemptsBaseRecords, 'Order Attempt'));
                                setShowTranscriptsTab(true);
                                setChartDetailHeaderInfo(`${orderAttemptRateMetrics.orderAttemptRate.toFixed(1)}% of ${orderAttemptRateMetrics.totalCalls.toLocaleString()} calls were order attempts`);
                                setIsChartDetailModalOpen(true);
                            }
                        }}
                        style={smallTileStyle}
                    >
                        <TileHeader style={{ justifyContent: 'center' }}>
                            <TileTitle small style={compactTileTitleStyle}>Order Attempt Rate</TileTitle>
                        </TileHeader>
                        {isMetricTilesLoading ? (
                            <NumberLoadingWrapper>
                                <SecondaryLoader className="grey" />
                            </NumberLoadingWrapper>
                        ) : (
                            <NumberDisplay>
                                <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>{orderAttemptRateMetrics.orderAttemptRate.toFixed(1)}%</BigNumber>
                                <NumberSubtext style={compactSubtextStyle}>{`${orderAttemptRateMetrics.orderAttemptCalls.toLocaleString()} of ${orderAttemptRateMetrics.totalCalls.toLocaleString()} calls`}</NumberSubtext>
                            </NumberDisplay>
                        )}
                        {hoveredTile === 'orderAttemptRate' && !isMetricTilesLoading && isInteractiveFeaturesReady && (
                            <ChartTooltip
                                title="Order Attempt Rate"
                                value={`${orderAttemptRateMetrics.orderAttemptRate.toFixed(1)}%`}
                                subtitle={`${orderAttemptRateMetrics.orderAttemptCalls.toLocaleString()} of ${orderAttemptRateMetrics.totalCalls.toLocaleString()} calls were order attempts`}
                                apps={orderAttemptRateMetrics.apps}
                                valueLabel="order attempts"
                                showLanguageBreakdown={isMultiLingual}
                                spanishOnly={isSpanishOnlyFilter}
                                clickHint={true}
                                compact={isMobile}
                                overlay={isMobile}
                            />
                        )}
                    </SmallNumberTile>
                )}

                {/* Labor Time Saved */}
                <SmallNumberTile
                    onMouseEnter={() => setHoveredTile('laborTime')}
                    onMouseLeave={() => setHoveredTile(null)}
                    onClick={() => {
                        if (!isMetricTilesLoading && !moment(endDate).isBefore('2026-02-01')) {
                            setChartDetailTitle('Labor Time Saved');
                            setChartDetailValueLabel('hours saved');
                            setChartDetailCountPrefix('');
                            setChartDetailShowPercentOfTotal(false);
                            setChartDetailSummaryLabel('Labor Time Saved');
                            setSelectedFunnelData([{
                                title: 'Labor Time Saved',
                                value: isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved,
                                subtitle: `${formatHoursAndMinutes(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved)} saved across all locations`,
                                apps: isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSavedApps : foodOrderingMetrics.laborHoursSavedApps?.map(app => {
                                    const english = app.english !== undefined ? Math.round(app.english) : undefined;
                                    const spanish = app.spanish !== undefined ? Math.round(app.spanish) : undefined;
                                    const count = (english !== undefined && spanish !== undefined)
                                        ? english + spanish
                                        : Math.round(app.count);
                                    return {
                                        appName: (appNameMap && appNameMap[app.appName]) || app.appName,
                                        count,
                                        english,
                                        spanish
                                    };
                                }) || []
                            }]);
                            setChartDetailTranscripts([]);
                            setShowTranscriptsTab(false);
                            setIsChartDetailModalOpen(true);
                        }
                    }}
                    style={laborTileStyle}
                >
                    <TileHeader style={{ justifyContent: 'center' }}>
                        <TileTitle small style={compactTileTitleStyle}>Labor Time Saved</TileTitle>
                    </TileHeader>
                    {isMetricTilesLoading ? (
                        <NumberLoadingWrapper>
                            <SecondaryLoader className="grey" />
                        </NumberLoadingWrapper>
                    ) : moment(endDate).isBefore('2026-02-01') ? (
                        <EmptyStateMessage style={{ fontSize: '12px', padding: '8px', position: 'relative', background: 'transparent' }}>
                            Data available starting Feb 2026
                        </EmptyStateMessage>
                    ) : (
                        <NumberDisplay>
                            {Math.floor(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved) > 0 ? (
                                <>
                                    <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>
                                        {Math.floor(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved)}<span style={{ fontSize: isPlatformMode ? '0.42em' : '0.5em', color: color_shades_dark, fontWeight: 400 }}> hrs</span>
                                    </BigNumber>
                                    <NumberSubtext style={compactSubtextStyle}>
                                        and {Math.round(((isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved) - Math.floor(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved)) * 60)} min saved
                                    </NumberSubtext>
                                </>
                            ) : (
                                <>
                                    <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>
                                        {Math.round(((isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved) - Math.floor(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved)) * 60)}<span style={{ fontSize: isPlatformMode ? '0.42em' : '0.5em', color: color_shades_dark, fontWeight: 400 }}> min</span>
                                    </BigNumber>
                                    <NumberSubtext style={compactSubtextStyle}>saved</NumberSubtext>
                                </>
                            )}
                        </NumberDisplay>
                    )}
                    {hoveredTile === 'laborTime' && !isMetricTilesLoading && !moment(endDate).isBefore('2026-02-01') && isInteractiveFeaturesReady && (
                        <ChartTooltip
                            title="Labor Time Saved"
                            value={formatHoursAndMinutes(isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSaved : foodOrderingMetrics.laborHoursSaved)}
                            subtitle="saved"
                            apps={isSpanishOnlyFilter ? spanishFoodMetrics.laborHoursSavedApps : (foodOrderingMetrics.laborHoursSavedApps || []).map(app => {
                                const appId = Object.keys(appNameMap || {}).find(id => (appNameMap?.[id] || id) === app.appName) || app.appName;
                                const lang = engagementLangByApp[appId];
                                const hasApiLang = app.english !== undefined || app.spanish !== undefined;
                                let english = app.english !== undefined ? Math.round(app.english) : undefined;
                                let spanish = app.spanish !== undefined ? Math.round(app.spanish) : undefined;
                                if (!hasApiLang && lang) {
                                    const total = lang.english + lang.spanish;
                                    const rawCount = Math.round(app.count);
                                    if (total > 0 && rawCount > 0) {
                                        english = Math.round(rawCount * lang.english / total);
                                        spanish = Math.round(rawCount * lang.spanish / total);
                                    }
                                }
                                // Derive count from english + spanish so the total always equals the sum
                                const count = (english !== undefined && spanish !== undefined)
                                    ? english + spanish
                                    : Math.round(app.count);
                                return { ...app, appName: (appNameMap && appNameMap[app.appName]) || app.appName, count, english, spanish };
                            })}
                            valueLabel="hours"
                            showLanguageBreakdown={isMultiLingual}
                            spanishOnly={isSpanishOnlyFilter}
                            clickHint={true}
                            compact={isMobile}
                            overlay={isMobile}
                        />
                    )}
                </SmallNumberTile>

                {isPlatformMode && (
                    <SmallNumberTile
                        onMouseEnter={() => setHoveredTile('augmentedOrderAttemptRate')}
                        onMouseLeave={() => setHoveredTile(null)}
                        onClick={() => {
                            if (!isMetricTilesLoading) {
                                setChartDetailTitle('Augmented Order Attempt Rate');
                                setChartDetailValueLabel('order attempts');
                                setChartDetailCountPrefix('');
                                setChartDetailShowPercentOfTotal(true);
                                setChartDetailSummaryLabel('Eligible Order Attempt Conversations');
                                setSelectedFunnelData([{
                                    title: 'Eligible Order Attempts',
                                    value: augmentedOrderAttemptRateMetrics.orderAttemptCalls,
                                    subtitle: `${augmentedOrderAttemptRateMetrics.orderAttemptRate.toFixed(1)}% of ${augmentedOrderAttemptRateMetrics.eligibleCalls.toLocaleString()} eligible calls`,
                                    apps: augmentedOrderAttemptRateMetrics.apps
                                }]);
                                const eligibleOrderAttemptRecords = orderAttemptsBaseRecords.filter(record => record.category !== 'NO_INTERACTION' && !record.tags.includes('CALL_DURING_OFF_HOURS'));
                                setChartDetailTranscripts(callRecordsToTranscripts(eligibleOrderAttemptRecords, 'Eligible Order Attempt'));
                                setShowTranscriptsTab(true);
                                setChartDetailHeaderInfo(`${augmentedOrderAttemptRateMetrics.orderAttemptRate.toFixed(1)}% of ${augmentedOrderAttemptRateMetrics.eligibleCalls.toLocaleString()} eligible calls were order attempts`);
                                setIsChartDetailModalOpen(true);
                            }
                        }}
                        style={placeholderTileStyle}
                    >
                        <TileHeader style={{ justifyContent: 'center' }}>
                            <TileTitle small style={compactTileTitleStyle}>Augmented Attempt Rate</TileTitle>
                        </TileHeader>
                        {isMetricTilesLoading ? (
                            <NumberLoadingWrapper>
                                <SecondaryLoader className="grey" />
                            </NumberLoadingWrapper>
                        ) : (
                            <NumberDisplay>
                                <BigNumber style={{ ...smallTileBigNumberStyle, ...compactBigNumberStyle }}>{augmentedOrderAttemptRateMetrics.orderAttemptRate.toFixed(1)}%</BigNumber>
                                <NumberSubtext style={compactSubtextStyle}>{`${augmentedOrderAttemptRateMetrics.orderAttemptCalls.toLocaleString()} of ${augmentedOrderAttemptRateMetrics.eligibleCalls.toLocaleString()} eligible calls`}</NumberSubtext>
                            </NumberDisplay>
                        )}
                        {hoveredTile === 'augmentedOrderAttemptRate' && !isMetricTilesLoading && isInteractiveFeaturesReady && (
                            <ChartTooltip
                                title="Augmented Order Attempt Rate"
                                value={`${augmentedOrderAttemptRateMetrics.orderAttemptRate.toFixed(1)}%`}
                                subtitle={`${augmentedOrderAttemptRateMetrics.orderAttemptCalls.toLocaleString()} of ${augmentedOrderAttemptRateMetrics.eligibleCalls.toLocaleString()} eligible calls were order attempts`}
                                apps={augmentedOrderAttemptRateMetrics.apps}
                                valueLabel="order attempts"
                                showLanguageBreakdown={isMultiLingual}
                                spanishOnly={isSpanishOnlyFilter}
                                clickHint={true}
                                compact={isMobile}
                                overlay={isMobile}
                            />
                        )}
                    </SmallNumberTile>
                )}
            </SmallTilesRow>

            {/* Call Volume Histogram */}
            <WideChartTile>
                {isHistogramChartLoading && (
                    <ChartLoadingOverlay style={{ boxShadow: 'none', background: 'white' }}>
                        <SecondaryLoader className="grey" />
                    </ChartLoadingOverlay>
                )}
                {!isHistogramChartLoading && currentHistogramData.length === 0 && (
                    <EmptyStateMessage>
                        {isTodayDateRange
                            ? "Data may still be loading for today. Check back later — tomorrow will have all of today's data."
                            : "We couldn't find any call volume data for the selected date range and assistants."}
                    </EmptyStateMessage>
                )}
                <HistogramChartCard
                    title={isSpanishOnlyFilter ? "Spanish Calls" : "Call Volume"}
                    titleStyles={{
                        fontSize: '15px',
                        fontWeight: 600,
                        color: text_dark,
                        margin: '0 0 2px 0',
                        letterSpacing: '-0.2px',
                        textTransform: 'none'
                    }}
                    subtitle=""
                    data={[]}
                    secondaryData={[]}
                    showSecondary={false}
                    showPrimary={false}
                    isLoading={isHistogramChartLoading}
                    graphPadding={36}
                    graphBottomPadding={16}
                    scale={0.92}
                    showViewDropdown={!isSpanishOnlyFilter}
                    selectedView={selectedView}
                    onViewChange={(view) => {
                        if (!isSpanishOnlyFilter) {
                            engagementHistogramContainer.setSelectedView(view as EngagementHistogramView);
                        }
                    }}
                    disableViewDropdown={isSpanishOnlyFilter || isHistogramChartLoading}
                    hideSpanishCallsOption={!isMultiLingual}
                    hideConcurrentCallsOption={!isSingleAppSelected}
                    hideContainableCallsOption={true}
                    multiLineData={currentHistogramData}
                    onExportClick={() => {
                        if (isInteractiveFeaturesReady) {
                            if (isSpanishOnlyFilter) {
                                setIsSpanishCallsModalOpen(true);
                            } else if (selectedView === 'Order Calls') {
                                setIsOrderAttemptsModalOpen(true);
                            } else {
                                setIsCallDataModalOpen(true);
                            }
                        }
                    }}
                    exportButtonText="View Details"
                    legendTooltips={{
                        'Order Attempted': 'CONTAINABLE_CALLS_ORDERING',
                        'Order Placed': 'CONTAINED_CALLS_ORDERING',
                        'All Conversations': 'ALL_CALLS_ORDERING',
                        'Spanish Order Attempts': 'CONTAINABLE_CALLS_ORDERING',
                        'Spanish Placed Orders': 'CONTAINED_CALLS_ORDERING',
                        'All Spanish Calls': 'ALL_CALLS_ORDERING',
                        'Max Concurrent Calls': 'MAX_CONCURRENT_CALLS'
                    }}
                    legendCounts={{
                        'All Conversations': allConversationsCount
                    }}
                    viewOptionLabels={{
                        'Order Calls': 'Order Volume',
                        'Concurrent Calls': 'Max Concurrent Calls'
                    }}
                    renderLegendTooltip={(lineId, metricKey) => (
                        <MetricInfoTooltip metricKey={metricKey} iconSize={11} showCalculation={false} />
                    )}
                />
            </WideChartTile>

            {/* Chart Detail Modal */}
            <HideOnMobile>
                <ChartDetailModal
                    isVisible={isChartDetailModalOpen}
                    onClose={() => {
                        setIsChartDetailModalOpen(false);
                        setSelectedFunnelData(null);
                        setChartDetailTitle('');
                        setChartDetailValueLabel('conversations');
                        setChartDetailCountPrefix('');
                        setChartDetailShowPercentOfTotal(true);
                        setChartDetailSummaryLabel(undefined);
                        setChartDetailTranscripts([]);
                        setShowTranscriptsTab(false);
                        setChartDetailHeaderInfo(undefined);
                    }}
                    title={chartDetailTitle}
                    dateRange={{ startDate, endDate }}
                    data={selectedFunnelData || []}
                    valueLabel={chartDetailValueLabel}
                    countPrefix={chartDetailCountPrefix}
                    showLanguageBreakdown={isMultiLingual}
                    showPercentOfTotal={selectedEntityCount > 1 && chartDetailShowPercentOfTotal}
                    summaryLabel={chartDetailSummaryLabel}
                    transcriptRecords={chartDetailTranscripts}
                    showTranscriptsTab={showTranscriptsTab}
                    spanishOnly={isSpanishOnlyFilter}
                    hideSummary={true}
                    noSummaryText="Summary unavailable"
                    domain="FOOD_ORDERING"
                    headerInfoText={chartDetailHeaderInfo}
                />
            </HideOnMobile>

            {/* Call Data Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isCallDataModalOpen}
                    onClose={() => setIsCallDataModalOpen(false)}
                    title="All Conversations"
                    startDate={startDate}
                    endDate={endDate}
                    records={foodOrderingCallDataRecords}
                    domain="FOOD_ORDERING"
                    showCategoryColumn={true}
                    showTagsColumn={true}
                    exportFileName="all-calls"
                    spanishOnlyFilter={isSpanishOnlyFilter}
                />
            </HideOnMobile>

            {/* Transfers Modal */}
            <HideOnMobile>
                <TransferDataModal
                    isVisible={isTransfersModalOpen}
                    onClose={() => setIsTransfersModalOpen(false)}
                    title="Transfers"
                    startDate={startDate}
                    endDate={endDate}
                    records={foodOrderingTransferDataRecords}
                    exportFileName="transfers"
                    defaultExcludedReasons={[TransferReason.IMMEDIATE_TRANSFER]}
                    spanishOnlyFilter={isSpanishOnlyFilter}
                />
            </HideOnMobile>

            {/* Order Attempts Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isOrderAttemptsModalOpen}
                    onClose={() => setIsOrderAttemptsModalOpen(false)}
                    title="Order Attempts"
                    startDate={startDate}
                    endDate={endDate}
                    records={orderAttemptsBaseRecords}
                    domain="FOOD_ORDERING"
                    showCategoryColumn={false}
                    showTagsColumn={true}
                    exportFileName="order-attempts"
                />
            </HideOnMobile>

            {/* Placed Orders Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isPlacedOrdersModalOpen}
                    onClose={() => setIsPlacedOrdersModalOpen(false)}
                    title="Placed Orders"
                    startDate={startDate}
                    endDate={endDate}
                    records={placedOrdersBaseRecords}
                    domain="FOOD_ORDERING"
                    showCategoryColumn={false}
                    showTagsColumn={true}
                    exportFileName="placed-orders"
                    enableOrderTotalThresholdFilter={true}
                />
            </HideOnMobile>

            {/* Spanish Calls Modal */}
            <HideOnMobile>
                <CallDataModal
                    isVisible={isSpanishCallsModalOpen}
                    onClose={() => setIsSpanishCallsModalOpen(false)}
                    title="Spanish Calls"
                    startDate={startDate}
                    endDate={endDate}
                    records={spanishCallDataRecords}
                    domain="FOOD_ORDERING"
                    showCategoryColumn={true}
                    showTagsColumn={true}
                    exportFileName="spanish-calls"
                    spanishOnlyFilter={true}
                />
            </HideOnMobile>
        </DashboardGrid>
    );
};

// Wrap with providers
export const AIOrderingDashboardWithProvider: React.FC<AIOrderingDashboardProps> = (props) => {
    return (
        <HistogramContainer.Provider>
            <EngagementHistogramContainer.Provider>
                <AIOrderingDashboard {...props} />
            </EngagementHistogramContainer.Provider>
        </HistogramContainer.Provider>
    );
};

export default AIOrderingDashboard;
